import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import FloatingAccountantCLI from './FloatingAccountantCLI.js';
import './SpreadsheetViewer.css';

/**
 * Full-page viewer for accountant linked documents at /opo/document/:documentId.
 * BANK_POSITION and LOAN_POSITION tabs on document 1 render custom panels.
 */

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function displayMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES_SHORT[m - 1]}\n${y}`;
}
function fmt(val) {
  if (val == null || val === '') return '—';
  const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/[,$]/g, ''));
  if (isNaN(n)) return String(val);
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtCad(val) {
  if (val == null || val === '') return '—';
  const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/[,$]/g, ''));
  if (isNaN(n)) return '—';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const DEFAULT_PAID_BY_LABELS = {
  FRED: 'Fred (personal loan)',
  SEAN: 'Sean (contribution)',
  CIBC: 'CIBC (company account)',
  TRANSFER: 'Internal transfer',
  ASSET: 'Refund / credit',
  OPTIMUM: 'Optimum Realty Inc.',
};

function paidByLabel(value, labels = DEFAULT_PAID_BY_LABELS) {
  const key = String(value || '').trim();
  if (!key) return '—';
  return labels[key] || key;
}

/** Avoid scroll jump when refetching long bank-statement tables. */
function withPreservedScroll(run) {
  const y = typeof window !== 'undefined' ? window.scrollY : 0;
  return Promise.resolve(run()).finally(() => {
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  });
}

function applyBmoTxFields(tx, fields) {
  const next = { ...tx };
  if (typeof fields.isOptiprime === 'boolean') next.isOptiprime = fields.isOptiprime;
  if (typeof fields.allocation === 'string') next.allocation = fields.allocation;
  if (typeof fields.paidBy === 'string') next.paidBy = fields.paidBy;
  if (typeof fields.description === 'string') next.description = fields.description;
  if (typeof fields.notes === 'string') next.notes = fields.notes;
  if (fields.cashFlowEntryId !== undefined) next.cashFlowEntryId = fields.cashFlowEntryId;
  if (fields.cashFlowEntry !== undefined) next.cashFlowEntry = fields.cashFlowEntry;
  return next;
}

function mergeBmoTxFromDbRow(tx, row) {
  if (!row) return tx;
  return applyBmoTxFields(tx, {
    isOptiprime: !!row.is_optiprime,
    allocation: row.allocation != null ? String(row.allocation) : tx.allocation,
    paidBy: row.paid_by != null ? String(row.paid_by) : tx.paidBy,
    description: row.description != null ? String(row.description) : tx.description,
    notes: row.notes != null ? String(row.notes) : tx.notes,
  });
}

function applyCibcTxFields(tx, fields) {
  const next = { ...tx };
  if (typeof fields.allocation === 'string') next.allocation = fields.allocation;
  if (typeof fields.paidBy === 'string') next.paidBy = fields.paidBy;
  if (typeof fields.cfDescription === 'string') next.cfDescription = fields.cfDescription;
  if (fields.matchedCashFlowEntryId !== undefined) {
    next.matchedCashFlowEntryId = fields.matchedCashFlowEntryId;
    if (!fields.matchedCashFlowEntryId) next.matchedCashFlowEntry = null;
  }
  return next;
}

function mergeCibcTxFromDbRow(tx, row) {
  if (!row) return tx;
  return applyCibcTxFields(tx, {
    allocation: row.allocation != null ? String(row.allocation) : '',
    paidBy: row.paid_by != null ? String(row.paid_by) : '',
    cfDescription: row.cf_description != null ? String(row.cf_description) : '',
    matchedCashFlowEntryId: row.matched_cash_flow_entry_id ?? null,
  });
}

/** Section headings under OPERATING EXPENSES on Statement of Operations (column A) — distinct styling in the web viewer */
const SO_SECTION_CATEGORY_LABELS = new Set([
  'Research & Development / Technology',
  'Startup, Incorporation & Web Infrastructure',
  'Sales & Marketing',
  'Bank & Finance Charges',
  'Depreciation & Amortization',
]);

function isStatementOfOperationsCategoryCell(activeSheet, colIdx, cellVal) {
  if (activeSheet !== 'Statement of Operations' || colIdx !== 0) return false;
  const t = String(cellVal ?? '').trim();
  return SO_SECTION_CATEGORY_LABELS.has(t);
}

const SCHEDULE_SHEETS = ['CAPITAL_ASSETS', 'STARTUP_COSTS', 'OPERATING_COSTS'];

/** Sidebar / nav labels (internal sheet id → display name). */
const WORKBOOK_TAB_LABELS = {
  BANK_POSITION: 'CIBC POSITION',
};

const RESIZABLE_FINANCIAL_STATEMENT_SHEETS = [
  'Statement of Operations',
  'Balance Sheet',
  'Changes in Equity',
  'Cash Flows — ASPE',
  'Notes',
  ...SCHEDULE_SHEETS,
];

const DEFAULT_COL_WIDTHS_BY_SHEET = {
  Notes: [720, 112, 112],
  'Statement of Operations': [560, 48, 128],
  'Balance Sheet': [520, 48, 128],
  'Changes in Equity': [400, 160, 160],
  'Cash Flows — ASPE': [520, 128, 48],
  CAPITAL_ASSETS: [520, 128],
  STARTUP_COSTS: [520, 128],
  OPERATING_COSTS: [520, 128],
};

function spreadsheetRowHasContent(row) {
  if (!Array.isArray(row)) return false;
  return row.some((c) => c !== '' && c != null);
}

function colWidthsStorageKey(sheetName) {
  return `opo-spreadsheet-col-widths:${sheetName}`;
}

function cellLooksMoney(val) {
  const s = String(val ?? '').trim().replace(/[\u2212\u2013]/g, '-');
  if (!s) return false;
  // $1,234.56 | -$1,234.56 | $-1,234.56 | ($1,234.56) | 1,234.56
  return /^\$?-?[\d,]+(\.\d{1,2})?$/.test(s)
    || /^-\$[\d,]+(\.\d{1,2})?$/.test(s)
    || /^\(\$?-?[\d,]+(\.\d{1,2})?\)$/.test(s);
}

function classifyStatementRow(sheetName, row, rIdx) {
  const a = String(row[0] ?? '').trim();
  const b = String(row[1] ?? '').trim();
  const c = String(row[2] ?? '').trim();
  const aUp = a.toUpperCase();

  if (rIdx < 4 && (/^OPTIPRIME|^NOTES TO|^STATEMENT|^From Inception|^For the Year|^As at|Unaudited/i.test(a))) {
    return 'spreadsheet-viewer-stmt-row-dochead';
  }

  if (sheetName === 'Notes') {
    if (/^NOTE \d+\s*[—–-]/i.test(a)) return 'spreadsheet-viewer-stmt-row-title';
    if (/^\([a-g]\)\s/i.test(a)) return 'spreadsheet-viewer-stmt-row-subhead';
    if (!a && b && (/^(Shares|Amount|Cost|Terms)$/i.test(b) || /Accum/i.test(c))) {
      return 'spreadsheet-viewer-stmt-row-table-hdr';
    }
    if (/^Total share capital|^Total shareholder loans|^Total cash outlays|^Net P&L expense|^Total funded|^CIBC closing/i.test(a)) {
      return 'spreadsheet-viewer-stmt-row-total';
    }
    if (b && (cellLooksMoney(b) || /^[\d,]+\.\d{2}$/.test(b)) && a) return 'spreadsheet-viewer-stmt-row-line';
    return 'spreadsheet-viewer-stmt-row-body';
  }

  if (sheetName === 'Statement of Operations') {
    if (/^REVENUE$|^OPERATING EXPENSES$/i.test(aUp)) return 'spreadsheet-viewer-stmt-row-major';
    if (isStatementOfOperationsCategoryCell(sheetName, 0, row[0])) return 'spreadsheet-viewer-stmt-row-section';
    if (/^TOTAL |^NET LOSS|^Services Invoiced|^Cash collected|^Note:/i.test(a)) return 'spreadsheet-viewer-stmt-row-total';
    if (cellLooksMoney(c) || cellLooksMoney(b)) return 'spreadsheet-viewer-stmt-row-line';
    return 'spreadsheet-viewer-stmt-row-body';
  }

  if (sheetName === 'Balance Sheet') {
    if (/^ASSETS$|^LIABILITIES AND SHAREHOLDERS|^TOTAL ASSETS$|^TOTAL LIABILITIES AND SHAREHOLDERS/i.test(aUp)) {
      return 'spreadsheet-viewer-stmt-row-major';
    }
    if (/^Current Assets|^Non-Current Assets|^Shareholders' Equity|^Current Liabilities/i.test(a)) {
      return 'spreadsheet-viewer-stmt-row-section';
    }
    if (/^TOTAL /i.test(a)) return 'spreadsheet-viewer-stmt-row-total';
    if (cellLooksMoney(c) || cellLooksMoney(b)) return 'spreadsheet-viewer-stmt-row-line';
    return 'spreadsheet-viewer-stmt-row-body';
  }

  if (sheetName === 'Changes in Equity') {
    if (!a && b && /Deficit|Share Capital|Total Equity/i.test(b)) return 'spreadsheet-viewer-stmt-row-table-hdr';
    if (/^Balance at|^Net loss|^Share capital issued/i.test(a)) return 'spreadsheet-viewer-stmt-row-total';
    if (cellLooksMoney(b) || cellLooksMoney(c) || b === '—') return 'spreadsheet-viewer-stmt-row-line';
    return 'spreadsheet-viewer-stmt-row-body';
  }

  if (sheetName === 'Cash Flows — ASPE') {
    if (/^CASH FLOWS FROM/i.test(aUp)) return 'spreadsheet-viewer-stmt-row-major';
    if (/^NET CASH |^NET INCREASE|^Cash, beginning|^Cash, end/i.test(a)) return 'spreadsheet-viewer-stmt-row-total';
    if (/^  Adjustments|^  Proceeds|^  Purchase|^  Depreciation|^Reconciliation/i.test(a)) {
      return 'spreadsheet-viewer-stmt-row-section';
    }
    if (cellLooksMoney(c) || cellLooksMoney(b)) return 'spreadsheet-viewer-stmt-row-line';
    return 'spreadsheet-viewer-stmt-row-body';
  }

  if (SCHEDULE_SHEETS.includes(sheetName)) {
    if (rIdx < 4 && (
      /^OPTIPRIME/i.test(a)
      || /Register|Schedule|Unaudited|Supplementary/i.test(a)
      || /^\(.*\)$/.test(a)
      || (rIdx <= 1 && a.length > 0)
    )) {
      return 'spreadsheet-viewer-stmt-row-dochead';
    }
    if (
      /Vendor\s*\/|Category|Amount \(CAD\)|Cost \(CAD\)|Description/i.test(`${a} ${b}`)
      || (/^Amount|^Cost \(CAD\)/i.test(b) && a)
    ) {
      return 'spreadsheet-viewer-stmt-row-table-hdr';
    }
    if (/^──|^--/.test(a)) return 'spreadsheet-viewer-stmt-row-section';
    if (/^— No |^— None /i.test(a)) return 'spreadsheet-viewer-stmt-row-body';
    if (/^TOTAL /i.test(a)) return 'spreadsheet-viewer-stmt-row-total';
    if (cellLooksMoney(b) || (typeof row[1] === 'number' && !Number.isNaN(row[1]))) {
      return 'spreadsheet-viewer-stmt-row-line';
    }
    return 'spreadsheet-viewer-stmt-row-body';
  }

  return 'spreadsheet-viewer-stmt-row-body';
}

function normalizeStatementColWidths(sheetName, colCount, prev) {
  const n = Math.max(colCount, 1);
  const defaults = DEFAULT_COL_WIDTHS_BY_SHEET[sheetName] || [480, 120, 120];
  let stored = null;
  try {
    const raw = localStorage.getItem(colWidthsStorageKey(sheetName));
    if (raw) stored = JSON.parse(raw);
  } catch (_) { /* ignore */ }
  const base = Array.isArray(prev) && prev.length === n
    ? prev
    : (Array.isArray(stored) && stored.length === n ? stored : null);
  const next = [];
  for (let i = 0; i < n; i++) {
    const fallback = defaults[i] ?? 100;
    const w = base && Number(base[i]) > 40 ? Number(base[i]) : fallback;
    next.push(w);
  }
  return next;
}

function ResizableStatementTable({ sheetName, rows, colCount }) {
  const [colWidths, setColWidths] = useState(() => normalizeStatementColWidths(sheetName, colCount, null));

  useEffect(() => {
    setColWidths((prev) => normalizeStatementColWidths(sheetName, colCount, prev));
  }, [colCount, sheetName]);

  const persistWidths = useCallback((widths) => {
    try {
      localStorage.setItem(colWidthsStorageKey(sheetName), JSON.stringify(widths));
    } catch (_) { /* ignore */ }
  }, [sheetName]);

  const onResizeStart = useCallback((colIndex, clientX) => {
    const startW = colWidths[colIndex];
    document.body.classList.add('spreadsheet-viewer-col-resizing');
    const onMove = (ev) => {
      const delta = ev.clientX - clientX;
      setColWidths((prev) => {
        const next = [...prev];
        next[colIndex] = Math.max(72, Math.round(startW + delta));
        return next;
      });
    };
    const onUp = () => {
      document.body.classList.remove('spreadsheet-viewer-col-resizing');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setColWidths((latest) => {
        persistWidths(latest);
        return latest;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [colWidths, persistWidths]);

  return (
    <div className="spreadsheet-viewer-table-wrap spreadsheet-viewer-table-wrap--stmt">
      <table className={`spreadsheet-viewer-table spreadsheet-viewer-table--stmt spreadsheet-viewer-table--${sheetName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
        <colgroup>
          {colWidths.map((w, i) => (
            <col key={i} style={{ width: `${w}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr className="spreadsheet-viewer-stmt-resize-row">
            {colWidths.map((w, i) => (
              <th key={i} className="spreadsheet-viewer-stmt-resize-cell" scope="col">
                <span className="spreadsheet-viewer-stmt-resize-label">{i === 0 ? 'Drag column edges' : ''}</span>
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize column ${i + 1}`}
                  className="spreadsheet-viewer-col-resizer"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onResizeStart(i, e.clientX);
                  }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rIdx) => (
            <tr key={rIdx} className={classifyStatementRow(sheetName, row, rIdx)}>
              {row.map((cell, cIdx) => {
                const isNum = (typeof cell === 'number' && !Number.isNaN(cell))
                  || (typeof cell === 'string' && cell.trim() !== '' && !Number.isNaN(Number(cell)));
                const isMoneyish = cellLooksMoney(cell);
                const isEmpty = cell === '' || cell == null;
                const isAmountColumn = cIdx >= 1 && (isNum || isMoneyish);
                const isSoCategory = isStatementOfOperationsCategoryCell(sheetName, cIdx, cell);
                return (
                  <td
                    key={cIdx}
                    className={`spreadsheet-viewer-cell spreadsheet-viewer-cell-wrap${isNum || isAmountColumn ? ' spreadsheet-viewer-cell-num' : ''}${isEmpty ? ' spreadsheet-viewer-cell-empty' : ''}${isSoCategory ? ' spreadsheet-viewer-cell-so-category' : ''}`}
                  >
                    {isEmpty ? '\u00A0' : String(cell)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** FYE April 30: Jan–Apr belong to that calendar year's FY; May–Dec to the next. */
function currentFiscalYearId(date = new Date()) {
  const y = date.getFullYear();
  const month = date.getMonth() + 1;
  const fyeYear = month <= 4 ? y : y + 1;
  return `FY${fyeYear}`;
}

function calendarYm(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Hide future FYs that have not started yet (keeps the selector to two clear years today). */
function visibleFiscalYears(years, currentId) {
  const ym = calendarYm();
  return (years || []).filter((y) => (
    y.id === currentId
    || y.isFirst
    || (y.monthFrom && y.monthFrom <= ym)
  ));
}

// ─────────────────────────────────────────────
// BankPositionPanel — CIBC business account
// Click a month chip to view its transactions; use the small ⬆ icon to upload.
// ─────────────────────────────────────────────

function BankPositionPanel({ onRefreshSheets, fy = '' }) {
  const [searchParams] = useSearchParams();
  const focusMonth = searchParams.get('month') || '';
  const focusTxId = Number(searchParams.get('tx') || 0) || null;
  const [bankData, setBankData]             = useState(null);
  const [loading, setLoading]               = useState(true);
  const [uploadingMonth, setUploadingMonth] = useState(null);
  const [addingMonth, setAddingMonth]       = useState(false);
  const [activeMonth, setActiveMonth]       = useState(focusMonth || null);
  const [activeMonthData, setActiveMonthData] = useState(null);
  const [monthLoading, setMonthLoading]     = useState(false);
  const [reExtracting, setReExtracting]     = useState(false);
  const [showRaw, setShowRaw]               = useState(false);
  const [options, setOptions]               = useState({ allocations: [], paidByOptions: [], paidByLabels: DEFAULT_PAID_BY_LABELS });

  const fileInputRef    = useRef(null);
  const uploadMonthRef  = useRef(null);

  useEffect(() => {
    setActiveMonth(focusMonth || null);
  }, [fy, focusMonth]);

  const fetchBankData = useCallback(() => {
    setLoading(true);
    const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
    fetch(`/api/opo/auditor/bank-position${qs}`)
      .then((r) => r.json())
      .then((d) => {
        setBankData(d);
        if (!activeMonth && !focusMonth) {
          const latest = (d.months || []).slice().reverse().find((m) => m.fileExists);
          if (latest) setActiveMonth(latest.month);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeMonth, fy, focusMonth]);

  const fetchMonthTransactions = useCallback((month, opts = {}) => {
    const { silent = false } = opts;
    if (!month) return Promise.resolve();
    if (!silent) setMonthLoading(true);
    return fetch(`/api/opo/auditor/cibc-statements/${month}/transactions`)
      .then((r) => r.json())
      .then((d) => setActiveMonthData(d))
      .catch(() => {})
      .finally(() => { if (!silent) setMonthLoading(false); });
  }, []);

  const fetchOptions = useCallback(() => {
    fetch('/api/opo/auditor/bmo-statements/options')
      .then((r) => r.json())
      .then((d) => setOptions({
        allocations: (d && d.allocations) || [],
        paidByOptions: (d && d.paidByOptions) || [],
        paidByLabels: { ...DEFAULT_PAID_BY_LABELS, ...((d && d.paidByLabels) || {}) },
      }))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchBankData(); fetchOptions(); }, [fetchBankData, fetchOptions]);
  useEffect(() => { if (activeMonth) fetchMonthTransactions(activeMonth); }, [activeMonth, fetchMonthTransactions]);
  useEffect(() => {
    if (!focusTxId || monthLoading || !activeMonthData) return;
    const el = document.getElementById(`cibc-tx-${focusTxId}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusTxId, monthLoading, activeMonthData]);

  const patchCibcTransaction = (tx, body) => {
    const optimistic = applyCibcTxFields(tx, body);
    setActiveMonthData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        transactions: prev.transactions.map((t) => (t.id === tx.id ? optimistic : t)),
      };
    });

    fetch(`/api/opo/auditor/cibc-statements/transactions/${tx.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => {
        if (!r.ok) throw new Error('patch failed');
        return r.json();
      })
      .then((d) => {
        if (d.transaction) {
          setActiveMonthData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              transactions: prev.transactions.map((t) =>
                t.id === tx.id ? mergeCibcTxFromDbRow(t, d.transaction) : t
              ),
            };
          });
        }
        if (body.matchedCashFlowEntryId !== undefined) {
          return withPreservedScroll(() => fetchMonthTransactions(activeMonth, { silent: true }));
        }
      })
      .catch(() => withPreservedScroll(() => fetchMonthTransactions(activeMonth, { silent: true })));
  };

  const handleCibcAllocationChange = (tx, allocation) => patchCibcTransaction(tx, { allocation });
  const handleCibcPaidByChange     = (tx, paidBy)     => patchCibcTransaction(tx, { paidBy });
  const handleCibcCfDescriptionChange = (tx, cfDescription) => patchCibcTransaction(tx, { cfDescription });
  const handleCibcMatchChange = (tx, cfIdStr) => {
    const cfId = cfIdStr ? Number(cfIdStr) : null;
    patchCibcTransaction(tx, { matchedCashFlowEntryId: cfId });
  };

  // Chip click → SELECT the month (not upload). Upload goes through the small ⬆ icon.
  const onChipClick = (month) => {
    if (!uploadingMonth) setActiveMonth(month);
  };

  const onChipUploadClick = (month, e) => {
    e.stopPropagation();
    uploadMonthRef.current = month;
    fileInputRef.current && fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    const month = uploadMonthRef.current;
    if (!file || !month) return;
    e.target.value = '';
    setUploadingMonth(month);
    const fd = new FormData();
    fd.append('file', file);
    fetch(`/api/opo/auditor/subagent/accountant/bank-statements/${month}/upload`, { method: 'POST', body: fd })
      .then((r) => r.json())
      .then(() => {
        setActiveMonth(month);
        fetchBankData();
        fetchMonthTransactions(month);
        onRefreshSheets && onRefreshSheets();
      })
      .catch(() => {})
      .finally(() => setUploadingMonth(null));
  };

  const handleAddMonth = () => {
    setAddingMonth(true);
    const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
    fetch(`/api/opo/auditor/subagent/accountant/bank-statements/add-next-month${qs}`, { method: 'POST' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Could not add month');
        return d;
      })
      .then(() => fetchBankData())
      .catch((err) => { window.alert(err.message || 'Could not add month'); })
      .finally(() => setAddingMonth(false));
  };

  const handleReExtract = () => {
    if (!activeMonth) return;
    setReExtracting(true);
    fetch(`/api/opo/auditor/cibc-statements/${activeMonth}/extract`, { method: 'POST' })
      .then((r) => r.json())
      .then(() => { fetchBankData(); fetchMonthTransactions(activeMonth); })
      .catch(() => {})
      .finally(() => setReExtracting(false));
  };

  if (loading) return <div className="bank-pos-loading">Loading CIBC position…</div>;

  const months        = bankData ? bankData.months        : [];
  const latestBalance = bankData ? bankData.latestBalance : null;
  const latestPeriod  = bankData ? bankData.latestPeriod  : null;

  const transactions = (activeMonthData && activeMonthData.transactions) || [];
  const warnings     = (activeMonthData && activeMonthData.warnings)     || [];
  const rawText      = (activeMonthData && activeMonthData.rawText)      || '';
  const totalsWd = transactions.reduce((s, t) => s + (t.withdrawals || 0), 0);
  const totalsDep = transactions.reduce((s, t) => s + (t.deposits || 0), 0);
  const monthClosing = transactions.filter((t) => t.balance != null).slice(-1)[0]?.balance;

  return (
    <div className="bank-pos-panel">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Balance summary cards */}
      <div className="bank-pos-cards">
        <div className="bank-pos-card bank-pos-card-live">
          <div className="bank-pos-card-label">Latest closing balance</div>
          <div className="bank-pos-card-value">{latestBalance != null ? fmtCad(latestBalance) : '—'}</div>
          <div className="bank-pos-card-sub">{latestPeriod || 'No statement parsed yet'}</div>
          <div className="bank-pos-card-source">Source: pdftotext on uploaded CIBC statement</div>
        </div>
        <div className="bank-pos-card bank-pos-card-account">
          <div className="bank-pos-card-label">Account</div>
          <div className="bank-pos-card-value" style={{ fontSize: '1rem' }}>CIBC Business Account</div>
          <div className="bank-pos-card-sub">Account #10-31805 · Branch 03300</div>
          <div className="bank-pos-card-source">CIBC</div>
        </div>
        <div className="bank-pos-card bank-pos-card-heartbeat">
          <div className="bank-pos-card-label">Heartbeat</div>
          <div className="bank-pos-card-value" style={{ fontSize: '0.95rem' }}>
            Statement → OCR → Match against Cash Flow
          </div>
          <div className="bank-pos-card-sub">Click any month to view its transactions and reconcile against the Cash Flow ledger.</div>
        </div>
      </div>

      {/* Month calendar */}
      <div className="bank-pos-cal-header">
        <span className="bank-pos-cal-title">Bank statements</span>
        <span className="bank-pos-cal-hint">Click a month to view extracted transactions · use the upload icon to attach a PDF</span>
      </div>
      <div className="bank-pos-cal-grid">
        {months.map((m) => {
          const isActive = activeMonth === m.month;
          return (
            <div
              key={m.month}
              className={`bmo-pos-chip-wrap${isActive ? ' bmo-pos-chip-wrap-active' : ''}`}
            >
              <button
                className={`bank-pos-chip${m.fileExists ? ' bank-pos-chip-ok' : ' bank-pos-chip-miss'}${uploadingMonth === m.month ? ' bank-pos-chip-loading' : ''}${isActive ? ' bank-pos-chip-active' : ''}`}
                onClick={() => onChipClick(m.month)}
                title={m.fileExists ? `${m.month}${m.balance != null ? ` — Closing: ${fmtCad(m.balance)}` : ''}` : `Click to select ${m.month} (no PDF yet)`}
                disabled={!!uploadingMonth}
              >
                <span className="bank-pos-chip-month">{displayMonth(m.month)}</span>
                {m.fileExists && <span className="bank-pos-chip-tick">✓</span>}
                {m.fileExists && m.balance != null && (
                  <span className="bank-pos-chip-bal">{fmtCad(m.balance)}</span>
                )}
                {uploadingMonth === m.month && <span className="bank-pos-chip-spinner" />}
              </button>
              <button
                className="bmo-pos-chip-upload"
                onClick={(e) => onChipUploadClick(m.month, e)}
                disabled={!!uploadingMonth}
                title={m.fileExists ? `Replace PDF for ${m.month}` : `Upload PDF for ${m.month}`}
              >
                ⬆
              </button>
            </div>
          );
        })}
        <button
          className="bank-pos-chip bank-pos-chip-add"
          onClick={handleAddMonth}
          disabled={addingMonth}
          title={fy ? `Add next month in ${fy}` : 'Add next month slot'}
        >
          {addingMonth ? '…' : '+ Add month'}
        </button>
      </div>

      {/* Per-month transaction review */}
      {activeMonth && (
        <div className="bmo-pos-month-section">
          <div className="bmo-pos-month-header">
            <div>
              <span className="bmo-pos-month-title">{displayMonth(activeMonth)} transactions</span>
              {activeMonthData && activeMonthData.extractedAt && (
                <span className="bmo-pos-month-sub">
                  · Extracted {new Date(activeMonthData.extractedAt).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              )}
              {monthClosing != null && (
                <span className="bmo-pos-month-sub">
                  · Closing balance <strong className="privacy-amt">{fmtCad(monthClosing)}</strong>
                </span>
              )}
            </div>
            <div className="bmo-pos-month-actions">
              <button
                className="bmo-pos-btn"
                onClick={handleReExtract}
                disabled={reExtracting || !activeMonthData || !activeMonthData.linkedDocumentId}
                title="Re-run pdftotext + parser on the stored PDF"
              >
                {reExtracting ? '…' : '↻ Re-extract'}
              </button>
              <button
                className="bmo-pos-btn bmo-pos-btn-secondary"
                onClick={() => setShowRaw((v) => !v)}
                disabled={!rawText}
                title="Toggle raw pdftotext output for parser debugging"
              >
                {showRaw ? 'Hide raw text' : 'Show raw text'}
              </button>
            </div>
          </div>

          {warnings.length > 0 && (
            <div className="bmo-pos-warnings">
              {warnings.map((w, i) => (
                <div key={i} className="bmo-pos-warning">⚠ {w}</div>
              ))}
            </div>
          )}

          {monthLoading ? (
            <div className="bank-pos-loading">Loading transactions…</div>
          ) : (
            <>
              {transactions.length === 0 ? (
                <div className="bmo-pos-empty">
                  {activeMonthData && activeMonthData.linkedDocumentId
                    ? 'No transactions parsed yet. Click ⬆ to upload the CIBC PDF for this month, or hit Re-extract.'
                    : 'No PDF uploaded yet. Click the ⬆ button on the month chip to upload your CIBC statement.'}
                </div>
              ) : (
                <div className="bmo-pos-table-wrap">
                  <table className="bmo-pos-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Bank description</th>
                        <th>CF description</th>
                        <th className="cur-month-num">Wd</th>
                        <th className="cur-month-num">Dep</th>
                        <th className="cur-month-num">Balance</th>
                        <th>Allocation</th>
                        <th>Paid by</th>
                        <th>Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => {
                        const matched = tx.matchedCashFlowEntry;
                        const allocValue = tx.allocation || '';
                        const paidByValue = tx.paidBy || '';
                        const cfDescValue = tx.cfDescription || '';
                        const matchId = tx.matchedCashFlowEntryId ? String(tx.matchedCashFlowEntryId) : '';
                        const hasMoney = (tx.withdrawals > 0) || (tx.deposits > 0);
                        return (
                          <tr
                            key={tx.id}
                            id={`cibc-tx-${tx.id}`}
                            className={`bmo-pos-row${matched ? ' bmo-pos-row-synced' : ''}${focusTxId === tx.id ? ' bmo-pos-row-focus' : ''}`}
                          >
                            <td className="cur-month-date">{tx.postedDate || '—'}</td>
                            <td title={tx.rawLine || ''} className="bmo-pos-desc-bank">{tx.description || '—'}</td>
                            <td className="bmo-pos-cell-cf-desc">
                              <input
                                type="text"
                                className="bmo-pos-input bmo-pos-input-cf-desc"
                                value={cfDescValue}
                                placeholder="Cash Flow description"
                                disabled={!hasMoney}
                                onChange={(e) => handleCibcCfDescriptionChange(tx, e.target.value)}
                              />
                            </td>
                            <td className="cur-month-num cur-month-wd">{tx.withdrawals > 0 ? fmtCad(tx.withdrawals) : '—'}</td>
                            <td className="cur-month-num cur-month-dep">{tx.deposits > 0 ? fmtCad(tx.deposits) : '—'}</td>
                            <td className="cur-month-num cur-month-bal">{tx.balance != null ? fmtCad(tx.balance) : '—'}</td>
                            <td className="bmo-pos-cell-dropdown">
                              <select
                                className="bmo-pos-select"
                                value={allocValue}
                                disabled={!hasMoney}
                                onChange={(e) => handleCibcAllocationChange(tx, e.target.value)}
                              >
                                <option value="">— select —</option>
                                {options.allocations.map((a) => (
                                  <option key={a} value={a}>{a}</option>
                                ))}
                                {allocValue && !options.allocations.includes(allocValue) && (
                                  <option value={allocValue}>{allocValue}</option>
                                )}
                              </select>
                            </td>
                            <td className="bmo-pos-cell-dropdown">
                              <select
                                className="bmo-pos-select"
                                value={paidByValue}
                                disabled={!hasMoney}
                                onChange={(e) => handleCibcPaidByChange(tx, e.target.value)}
                              >
                                <option value="">—</option>
                                {options.paidByOptions.map((p) => (
                                  <option key={p} value={p}>{paidByLabel(p, options.paidByLabels)}</option>
                                ))}
                                {paidByValue && !options.paidByOptions.includes(paidByValue) && (
                                  <option value={paidByValue}>{paidByLabel(paidByValue, options.paidByLabels)}</option>
                                )}
                              </select>
                            </td>
                            <td className="bmo-pos-cell-match">
                              {matchId ? (
                                <select
                                  className="bmo-pos-select bmo-pos-select-match"
                                  value={matchId}
                                  disabled={!hasMoney}
                                  onChange={(e) => handleCibcMatchChange(tx, e.target.value)}
                                  title={matched ? `Matched: ${matched.date} · ${fmtCad(matched.amount)}` : ''}
                                >
                                  <option value={matchId}>↳ CF #{matchId}</option>
                                  <option value="">Clear match</option>
                                </select>
                              ) : hasMoney ? (
                                <span className="bmo-pos-match-unlinked">
                                  <input
                                    type="number"
                                    className="bmo-pos-input bmo-pos-input-cf-id"
                                    placeholder="CF #"
                                    min="1"
                                    step="1"
                                    onBlur={(e) => {
                                      const v = e.target.value.trim();
                                      if (v) handleCibcMatchChange(tx, v);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const v = e.target.value.trim();
                                        if (v) handleCibcMatchChange(tx, v);
                                      }
                                    }}
                                  />
                                  <span className="bmo-pos-tag bmo-pos-tag-nomatch">no match</span>
                                </span>
                              ) : (
                                <span className="bmo-pos-tag bmo-pos-tag-skip">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="cur-month-footer">
                        <td colSpan={3}>
                          {transactions.length} rows · {transactions.filter((t) => t.matchedCashFlowEntryId).length} matched to Cash Flow
                        </td>
                        <td className="cur-month-num cur-month-wd">{fmtCad(totalsWd)}</td>
                        <td className="cur-month-num cur-month-dep">{fmtCad(totalsDep)}</td>
                        <td className="cur-month-num cur-month-bal">{monthClosing != null ? fmtCad(monthClosing) : '—'}</td>
                        <td colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {showRaw && rawText && (
                <div className="bmo-pos-raw-wrap">
                  <div className="bmo-pos-raw-header">Raw pdftotext output (debug)</div>
                  <pre className="bmo-pos-raw">{rawText}</pre>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// BmoPositionPanel — BMO personal-account upload + per-transaction extraction
// ─────────────────────────────────────────────

function BmoPositionPanel({ onRefreshSheets, fy = '' }) {
  const [searchParams] = useSearchParams();
  const focusMonth = searchParams.get('month') || '';
  const focusTxId = Number(searchParams.get('tx') || 0) || null;
  const [bmoData, setBmoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingMonth, setUploadingMonth] = useState(null);
  const [addingMonth, setAddingMonth] = useState(false);
  const [activeMonth, setActiveMonth] = useState(focusMonth || null);
  const [activeMonthData, setActiveMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [options, setOptions] = useState({ allocations: [], paidByOptions: [], paidByLabels: DEFAULT_PAID_BY_LABELS });
  const [autoTicking, setAutoTicking] = useState(false);

  const fileInputRef = useRef(null);
  const uploadMonthRef = useRef(null);

  useEffect(() => {
    setActiveMonth(focusMonth || null);
  }, [fy, focusMonth]);

  const fetchBmoData = useCallback(() => {
    setLoading(true);
    const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
    fetch(`/api/opo/auditor/bmo-position${qs}`)
      .then((r) => r.json())
      .then((d) => {
        setBmoData(d);
        if (!activeMonth && !focusMonth) {
          const latest = (d.months || []).slice().reverse().find((m) => m.fileExists);
          if (latest) setActiveMonth(latest.month);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeMonth, fy, focusMonth]);

  const fetchMonthTransactions = useCallback((month, opts = {}) => {
    const { silent = false } = opts;
    if (!month) return Promise.resolve();
    if (!silent) setMonthLoading(true);
    return fetch(`/api/opo/auditor/bmo-statements/${month}/transactions`)
      .then((r) => r.json())
      .then((d) => setActiveMonthData(d))
      .catch(() => {})
      .finally(() => { if (!silent) setMonthLoading(false); });
  }, []);

  const fetchOptions = useCallback(() => {
    fetch('/api/opo/auditor/bmo-statements/options')
      .then((r) => r.json())
      .then((d) => setOptions({
        allocations: (d && d.allocations) || [],
        paidByOptions: (d && d.paidByOptions) || [],
        paidByLabels: { ...DEFAULT_PAID_BY_LABELS, ...((d && d.paidByLabels) || {}) },
      }))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchBmoData(); fetchOptions(); }, [fetchBmoData, fetchOptions]);
  useEffect(() => { if (activeMonth) fetchMonthTransactions(activeMonth); }, [activeMonth, fetchMonthTransactions]);
  useEffect(() => {
    if (!focusTxId || monthLoading || !activeMonthData) return;
    const el = document.getElementById(`bmo-tx-${focusTxId}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusTxId, monthLoading, activeMonthData]);

  const onChipClick = (month) => {
    if (!uploadingMonth) {
      // Switch to view the month even if not yet uploaded.
      setActiveMonth(month);
    }
  };

  const onChipUploadClick = (month, e) => {
    e.stopPropagation();
    uploadMonthRef.current = month;
    fileInputRef.current && fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    const month = uploadMonthRef.current;
    if (!file || !month) return;
    e.target.value = '';
    setUploadingMonth(month);
    const fd = new FormData();
    fd.append('file', file);
    fetch(`/api/opo/auditor/bmo-statements/${month}/upload`, { method: 'POST', body: fd })
      .then((r) => r.json())
      .then(() => {
        setActiveMonth(month);
        fetchBmoData();
        fetchMonthTransactions(month);
      })
      .catch(() => {})
      .finally(() => setUploadingMonth(null));
  };

  const handleAddMonth = () => {
    setAddingMonth(true);
    const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
    fetch(`/api/opo/auditor/bmo-statements/add-next-month${qs}`, { method: 'POST' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Could not add month');
        return d;
      })
      .then((d) => {
        fetchBmoData();
        if (d && d.month) setActiveMonth(d.month);
      })
      .catch((err) => { window.alert(err.message || 'Could not add month'); })
      .finally(() => setAddingMonth(false));
  };

  const handleReExtract = () => {
    if (!activeMonth) return;
    setReExtracting(true);
    fetch(`/api/opo/auditor/bmo-statements/${activeMonth}/extract`, { method: 'POST' })
      .then((r) => r.json())
      .then(() => { fetchBmoData(); fetchMonthTransactions(activeMonth); })
      .catch(() => {})
      .finally(() => setReExtracting(false));
  };

  const patchTransaction = (tx, body) => {
    const optimistic = applyBmoTxFields(tx, body);
    setActiveMonthData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        transactions: prev.transactions.map((t) => (t.id === tx.id ? optimistic : t)),
      };
    });

    fetch(`/api/opo/auditor/bmo-statements/transactions/${tx.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => {
        if (!r.ok) throw new Error('patch failed');
        return r.json();
      })
      .then((d) => {
        if (d.transaction) {
          setActiveMonthData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              transactions: prev.transactions.map((t) =>
                t.id === tx.id ? mergeBmoTxFromDbRow(t, d.transaction) : t
              ),
            };
          });
        }
      })
      .catch(() => withPreservedScroll(() => fetchMonthTransactions(activeMonth, { silent: true })));
  };

  const toggleOptiprime = (tx) => patchTransaction(tx, { isOptiprime: !tx.isOptiprime });

  const handleAllocationChange = (tx, allocation) => patchTransaction(tx, { allocation });
  const handlePaidByChange     = (tx, paidBy)     => patchTransaction(tx, { paidBy });

  const handleAutoTickMatches = () => {
    setAutoTicking(true);
    fetch('/api/opo/auditor/bmo-statements/auto-tick-matched', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ month: activeMonth }),
    })
      .then((r) => r.json())
      .then(() => withPreservedScroll(() => {
        fetchBmoData();
        return fetchMonthTransactions(activeMonth, { silent: true });
      }))
      .catch(() => {})
      .finally(() => setAutoTicking(false));
  };

  const handleSyncToCashFlow = (tx) => {
    setSyncingId(tx.id);
    fetch(`/api/opo/auditor/bmo-statements/transactions/${tx.id}/sync`, { method: 'POST' })
      .then((r) => r.json())
      .then((d) => {
        if (d.cashFlowEntryId) {
          setActiveMonthData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              transactions: prev.transactions.map((t) =>
                t.id === tx.id
                  ? applyBmoTxFields(t, {
                      cashFlowEntryId: d.cashFlowEntryId,
                      cashFlowEntry: d.cashFlowEntry || t.cashFlowEntry,
                      isOptiprime: true,
                    })
                  : t
              ),
            };
          });
        }
        onRefreshSheets && onRefreshSheets();
      })
      .catch(() => {})
      .finally(() => setSyncingId(null));
  };

  const handleUnlinkFromCashFlow = (tx) => {
    setSyncingId(tx.id);
    fetch(`/api/opo/auditor/bmo-statements/transactions/${tx.id}/sync`, { method: 'DELETE' })
      .then((r) => r.json())
      .then(() => {
        setActiveMonthData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            transactions: prev.transactions.map((t) =>
              t.id === tx.id
                ? applyBmoTxFields(t, { cashFlowEntryId: null, cashFlowEntry: null })
                : t
            ),
          };
        });
        onRefreshSheets && onRefreshSheets();
      })
      .catch(() => {})
      .finally(() => setSyncingId(null));
  };

  if (loading) return <div className="bank-pos-loading">Loading BMO position…</div>;

  const months = (bmoData && bmoData.months) || [];
  const latestBalance = bmoData ? bmoData.latestBalance : null;
  const latestPeriod  = bmoData ? bmoData.latestPeriod  : null;

  const transactions = (activeMonthData && activeMonthData.transactions) || [];
  const warnings     = (activeMonthData && activeMonthData.warnings) || [];
  const rawText      = (activeMonthData && activeMonthData.rawText) || '';
  const totalsWd = transactions.reduce((s, t) => s + (t.withdrawals || 0), 0);
  const totalsDep = transactions.reduce((s, t) => s + (t.deposits || 0), 0);
  const totalOp = transactions.filter((t) => t.isOptiprime).reduce((s, t) => s + (t.withdrawals || 0) - (t.deposits || 0), 0);

  return (
    <div className="bank-pos-panel bmo-pos-panel">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Balance cards */}
      <div className="bank-pos-cards">
        <div className="bank-pos-card bank-pos-card-live">
          <div className="bank-pos-card-label">Latest closing balance</div>
          <div className="bank-pos-card-value">{latestBalance != null ? fmtCad(latestBalance) : '—'}</div>
          <div className="bank-pos-card-sub">{latestPeriod || 'No statement parsed yet'}</div>
          <div className="bank-pos-card-source">Source: OCR / pdftotext on uploaded BMO statement</div>
        </div>
        <div className="bank-pos-card bank-pos-card-account">
          <div className="bank-pos-card-label">Account</div>
          <div className="bank-pos-card-value" style={{ fontSize: '1rem' }}>BMO Personal Account</div>
          <div className="bank-pos-card-sub">Fred Goss · personal account used for OptiPrime expenses</div>
          <div className="bank-pos-card-source">BMO</div>
        </div>
        <div className="bank-pos-card bank-pos-card-heartbeat">
          <div className="bank-pos-card-label">Heartbeat</div>
          <div className="bank-pos-card-value" style={{ fontSize: '0.95rem' }}>
            Statement → OCR → Tick → Cash Flow → ASPE
          </div>
          <div className="bank-pos-card-sub">Each ticked row becomes a Cash Flow entry and re-runs the financial sync.</div>
        </div>
      </div>

      {/* Month calendar */}
      <div className="bank-pos-cal-header">
        <span className="bank-pos-cal-title">BMO statements</span>
        <span className="bank-pos-cal-hint">Click a month to review extracted transactions · use the upload icon to attach a PDF</span>
      </div>
      <div className="bank-pos-cal-grid">
        {months.map((m) => {
          const isActive = activeMonth === m.month;
          return (
            <div
              key={m.month}
              className={`bmo-pos-chip-wrap${isActive ? ' bmo-pos-chip-wrap-active' : ''}`}
            >
              <button
                className={`bank-pos-chip${m.fileExists ? ' bank-pos-chip-ok' : ' bank-pos-chip-miss'}${uploadingMonth === m.month ? ' bank-pos-chip-loading' : ''}${isActive ? ' bmo-pos-chip-active' : ''}`}
                onClick={() => onChipClick(m.month)}
                title={`${m.month} — ${m.transactionCount || 0} txns, ${m.optiprimeCount || 0} OptiPrime${m.balance != null ? `, balance ${fmtCad(m.balance)}` : ''}`}
                disabled={!!uploadingMonth}
              >
                <span className="bank-pos-chip-month">{displayMonth(m.month)}</span>
                {m.fileExists && <span className="bank-pos-chip-tick">✓</span>}
                {m.fileExists && m.transactionCount > 0 && (
                  <span className="bmo-pos-chip-counts">
                    {m.optiprimeCount}/{m.transactionCount}
                  </span>
                )}
                {uploadingMonth === m.month && <span className="bank-pos-chip-spinner" />}
              </button>
              <button
                className="bmo-pos-chip-upload"
                onClick={(e) => onChipUploadClick(m.month, e)}
                disabled={!!uploadingMonth}
                title={m.fileExists ? `Replace PDF for ${m.month}` : `Upload PDF for ${m.month}`}
              >
                ⬆
              </button>
            </div>
          );
        })}
        <button
          className="bank-pos-chip bank-pos-chip-add"
          onClick={handleAddMonth}
          disabled={addingMonth}
          title={fy ? `Add next month in ${fy}` : 'Add next month slot'}
        >
          {addingMonth ? '…' : '+ Add month'}
        </button>
      </div>

      {/* Per-month transaction review */}
      {activeMonth && (
        <div className="bmo-pos-month-section">
          <div className="bmo-pos-month-header">
            <div>
              <span className="bmo-pos-month-title">{displayMonth(activeMonth)} transactions</span>
              {activeMonthData && activeMonthData.extractedAt && (
                <span className="bmo-pos-month-sub">
                  · Extracted {new Date(activeMonthData.extractedAt).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              )}
            </div>
            <div className="bmo-pos-month-actions">
              <button
                className="bmo-pos-btn"
                onClick={handleAutoTickMatches}
                disabled={autoTicking || !transactions.some((t) => t.matchedCashFlowEntryId && !t.isOptiprime)}
                title="Tick OptiPrime on every row that has an auto-match against the existing Cash Flow ledger"
              >
                {autoTicking ? '…' : '✓ Auto-tick matches'}
              </button>
              <button
                className="bmo-pos-btn"
                onClick={handleReExtract}
                disabled={reExtracting || !activeMonthData || !activeMonthData.linkedDocumentId}
                title="Re-run pdftotext + parser on the stored PDF"
              >
                {reExtracting ? '…' : '↻ Re-extract'}
              </button>
              <button
                className="bmo-pos-btn bmo-pos-btn-secondary"
                onClick={() => setShowRaw((v) => !v)}
                disabled={!rawText}
                title="Toggle raw pdftotext output for parser debugging"
              >
                {showRaw ? 'Hide raw text' : 'Show raw text'}
              </button>
            </div>
          </div>

          {warnings.length > 0 && (
            <div className="bmo-pos-warnings">
              {warnings.map((w, i) => (
                <div key={i} className="bmo-pos-warning">⚠ {w}</div>
              ))}
            </div>
          )}

          {monthLoading ? (
            <div className="bank-pos-loading">Loading transactions…</div>
          ) : (
            <>
              {transactions.length === 0 ? (
                <div className="bmo-pos-empty">
                  {activeMonthData && activeMonthData.linkedDocumentId
                    ? 'No transactions parsed yet. Click ⬆ to upload the BMO PDF for this month, or hit Re-extract.'
                    : 'No PDF uploaded yet. Click the ⬆ button on the month chip to upload your BMO statement.'}
                </div>
              ) : (
                <div className="bmo-pos-table-wrap">
                  <table className="bmo-pos-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th className="cur-month-num">Wd</th>
                        <th className="cur-month-num">Dep</th>
                        <th className="cur-month-num">Balance</th>
                        <th className="bmo-pos-th-tick">OptiPrime?</th>
                        <th>Allocation</th>
                        <th>Paid by</th>
                        <th>Match</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => {
                        const isSynced  = !!tx.cashFlowEntryId;
                        const matched   = tx.matchedCashFlowEntry;
                        const cf        = matched || tx.cashFlowEntry;
                        const cfContext = cf
                          ? `${cf.description || ''} · ${cf.allocation || '—'} · ${cf.paid_by || '—'}`
                          : '';
                        const allocValue = tx.allocation || (cf ? cf.allocation : '') || '';
                        const paidByValue = tx.paidBy    || (cf ? cf.paid_by    : '') || '';
                        const hasMoney = (tx.withdrawals > 0) || (tx.deposits > 0);
                        return (
                          <tr
                            key={tx.id}
                            id={`bmo-tx-${tx.id}`}
                            className={`bmo-pos-row${isSynced ? ' bmo-pos-row-synced' : ''}${tx.isOptiprime && !isSynced ? ' bmo-pos-row-op' : ''}${focusTxId === tx.id ? ' bmo-pos-row-focus' : ''}`}
                          >
                            <td className="cur-month-date">{tx.postedDate || '—'}</td>
                            <td title={tx.rawLine || ''} className="bmo-pos-cell-desc">
                              <div className="bmo-pos-desc-bank">{tx.description || '—'}</div>
                              {cfContext && (
                                <div className="bmo-pos-desc-cf" title={cfContext}>(CF: {cfContext})</div>
                              )}
                            </td>
                            <td className="cur-month-num cur-month-wd">{tx.withdrawals > 0 ? fmtCad(tx.withdrawals) : '—'}</td>
                            <td className="cur-month-num cur-month-dep">{tx.deposits > 0 ? fmtCad(tx.deposits) : '—'}</td>
                            <td className="cur-month-num cur-month-bal">{tx.balance != null ? fmtCad(tx.balance) : '—'}</td>
                            <td className="bmo-pos-cell-tick">
                              <input
                                type="checkbox"
                                checked={!!tx.isOptiprime}
                                onChange={() => toggleOptiprime(tx)}
                                disabled={isSynced || !hasMoney}
                                title={isSynced ? 'Already synced to Cash Flow' : 'Mark as OptiPrime-related'}
                              />
                            </td>
                            <td className="bmo-pos-cell-dropdown">
                              <select
                                className="bmo-pos-select"
                                value={allocValue}
                                disabled={isSynced || !hasMoney}
                                onChange={(e) => handleAllocationChange(tx, e.target.value)}
                              >
                                <option value="">— select —</option>
                                {options.allocations.map((a) => (
                                  <option key={a} value={a}>{a}</option>
                                ))}
                                {allocValue && !options.allocations.includes(allocValue) && (
                                  <option value={allocValue}>{allocValue}</option>
                                )}
                              </select>
                            </td>
                            <td className="bmo-pos-cell-dropdown">
                              <select
                                className="bmo-pos-select"
                                value={paidByValue}
                                disabled={isSynced || !hasMoney}
                                onChange={(e) => handlePaidByChange(tx, e.target.value)}
                              >
                                <option value="">—</option>
                                {options.paidByOptions.map((p) => (
                                  <option key={p} value={p}>{paidByLabel(p, options.paidByLabels)}</option>
                                ))}
                                {paidByValue && !options.paidByOptions.includes(paidByValue) && (
                                  <option value={paidByValue}>{paidByLabel(paidByValue, options.paidByLabels)}</option>
                                )}
                              </select>
                            </td>
                            <td className="bmo-pos-cell-match">
                              {isSynced ? (
                                <span className="bmo-pos-tag bmo-pos-tag-synced">↳ CF #{tx.cashFlowEntryId}</span>
                              ) : matched ? (
                                <span className="bmo-pos-tag bmo-pos-tag-match" title={`Matched: ${matched.date} · ${matched.description} · ${fmtCad(matched.amount)}`}>
                                  ≈ CF #{matched.id}
                                </span>
                              ) : hasMoney ? (
                                <span className="bmo-pos-tag bmo-pos-tag-nomatch">no match</span>
                              ) : (
                                <span className="bmo-pos-tag bmo-pos-tag-skip">—</span>
                              )}
                            </td>
                            <td className="bmo-pos-cell-actions">
                              {isSynced ? (
                                <button
                                  className="bmo-pos-link-btn"
                                  onClick={() => handleUnlinkFromCashFlow(tx)}
                                  disabled={syncingId === tx.id}
                                  title="Remove from Cash Flow"
                                >
                                  {syncingId === tx.id ? '…' : 'Unlink'}
                                </button>
                              ) : hasMoney ? (
                                <button
                                  className="bmo-pos-sync-btn"
                                  onClick={() => handleSyncToCashFlow(tx)}
                                  disabled={syncingId === tx.id || !tx.isOptiprime || !allocValue || !paidByValue}
                                  title={
                                    !tx.isOptiprime
                                      ? 'Tick OptiPrime first'
                                      : !allocValue
                                        ? 'Select an allocation'
                                        : !paidByValue
                                          ? 'Select who paid'
                                          : 'Push to Cash Flow + refresh ASPE statements'
                                  }
                                >
                                  {syncingId === tx.id ? '…' : '→ Cash Flow'}
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="cur-month-footer">
                        <td colSpan={2}>
                          {transactions.length} rows · {transactions.filter((t) => t.isOptiprime).length} OptiPrime · {transactions.filter((t) => t.cashFlowEntryId).length} synced
                        </td>
                        <td className="cur-month-num cur-month-wd">{fmtCad(totalsWd)}</td>
                        <td className="cur-month-num cur-month-dep">{fmtCad(totalsDep)}</td>
                        <td className="cur-month-num cur-month-bal">{fmtCad(totalOp)} <span className="bmo-pos-foot-sub">OptiPrime net</span></td>
                        <td colSpan={5}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {showRaw && rawText && (
                <div className="bmo-pos-raw-wrap">
                  <div className="bmo-pos-raw-header">Raw pdftotext output (debug)</div>
                  <pre className="bmo-pos-raw">{rawText}</pre>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// LoanPositionPanel
// ─────────────────────────────────────────────

// The two founders. Non-interest-bearing shareholder loans (ASPE Note 5 wording).
const FOUNDERS = ['Willem Frederik Goss', 'Xiangyu Li (Sean)'];
const SHAREHOLDER_LOAN_TERMS =
  'Non-interest bearing; no fixed repayment date (repayment when agreed between the Company and the lender)';
const LOAN_STATUS_OPTIONS = ['Outstanding', 'Partially repaid', 'Repaid', 'Forgiven'];
// Column order in XLSX: Date, Lender, Amount (CAD), Purpose, Interest Rate (%), Due Date, Status, Notes
const LOAN_COLS = ['Date', 'Lender', 'Amount (CAD)', 'Purpose', 'Status', 'Notes'];

const EMPTY_LOAN = {
  date: new Date().toISOString().slice(0, 10),
  lender: FOUNDERS[0],
  amount: '',
  purpose: '',
  status: 'Outstanding',
  notes: '',
};

function loanBreakdownAmountClass(amount, direction) {
  if (direction === 'total' || direction === 'neutral') return 'loan-bd-amt-neu';
  if (direction === 'decrease' || amount < 0) return 'loan-bd-amt-dec';
  if (direction === 'increase' || amount > 0) return 'loan-bd-amt-inc';
  return 'loan-bd-amt-neu';
}

function LoanFormula({ lines }) {
  if (!lines.length) return null;
  return (
    <div className="loan-bd-formula">
      <div className="loan-bd-formula-title">How this balance is calculated</div>
      <table className="loan-bd-formula-table">
        <tbody>
          {lines.map((ln, i) => {
            const isTotal = ln.direction === 'total';
            const showNeg = ln.amount < 0;
            const amt = showNeg ? `(${fmtCad(Math.abs(ln.amount))})` : fmtCad(ln.amount);
            return (
              <tr key={`formula-${i}`} className={isTotal ? 'loan-bd-formula-total' : ''}>
                <td>
                  {ln.label}
                  {ln.note ? <span className="loan-bd-note">{ln.note}</span> : null}
                </td>
                <td className={`loan-bd-amt ${loanBreakdownAmountClass(ln.amount, ln.direction)}`}>{amt}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LoanBreakdownDetail({ lines }) {
  if (!lines || !lines.length) return null;
  const formula = lines.filter((ln) => ln.kind === 'formula');
  const detail = lines.filter((ln) => ln.kind !== 'formula');
  return (
    <div className="loan-bd-wrap">
      <LoanFormula lines={formula} />
      <table className="loan-bd-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Source</th>
            <th>Line</th>
            <th className="loan-bd-th-amt">Amount (CAD)</th>
          </tr>
        </thead>
        <tbody>
          {detail.map((ln, i) => {
            const isHeader = ln.kind === 'header';
            const isTotal = ln.kind === 'total';
            const isSubtotal = ln.kind === 'subtotal';
            const rowCls = [
              isHeader ? 'loan-bd-row-header' : '',
              isTotal ? 'loan-bd-row-total' : '',
              isSubtotal ? 'loan-bd-row-subtotal' : '',
              ln.kind === 'info' ? 'loan-bd-row-info' : '',
            ].filter(Boolean).join(' ');
            const showAmt = ln.kind !== 'header' && ln.amount != null && (ln.amount !== 0 || isSubtotal || isTotal);
            const amtDisplay = showAmt
              ? (ln.amount < 0 ? `(${fmtCad(Math.abs(ln.amount))})` : fmtCad(ln.amount))
              : '';
            const noteText = ln.kind === 'total' && ln.note ? ln.note : ln.note;
            return (
              <tr key={`${ln.kind}-${i}`} className={rowCls}>
                <td className="loan-bd-date">{ln.date || ''}</td>
                <td className="loan-bd-src">{ln.source || ''}</td>
                <td className="loan-bd-label">
                  {ln.label}
                  {noteText ? (
                    <span className={ln.kind === 'total' ? 'loan-bd-note loan-bd-note-total' : 'loan-bd-note'}>
                      {noteText}
                    </span>
                  ) : null}
                </td>
                <td className={`loan-bd-amt ${showAmt ? loanBreakdownAmountClass(ln.amount, ln.direction) : ''}`}>
                  {amtDisplay}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LoanPositionPanel({ onRefreshSheets }) {
  const [loans, setLoans]       = useState([]);
  const [bankSummary, setBankSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft]       = useState(EMPTY_LOAN);
  const [saving, setSaving]     = useState(false);
  const [saveErr, setSaveErr]   = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchLoans = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/opo/auditor/loans').then((r) => r.json()),
      fetch('/api/opo/auditor/cashflow-summary').then((r) => r.json()),
      fetch('/api/opo/auditor/loan-position-breakdown').then((r) => r.json()),
    ])
      .then(([loanData, summary, bd]) => {
        setLoans(loanData.loans || []);
        setBankSummary(summary);
        setBreakdown(bd && bd.founders ? bd : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  // Bank-truth balances (primary); fall back to loan register rows if summary not loaded
  const totalByFounder = {
    'Willem Frederik Goss': bankSummary ? bankSummary.fred.personalContribution : 0,
    'Xiangyu Li (Sean)': bankSummary ? bankSummary.sean.loan : 0,
  };
  if (!bankSummary) {
    FOUNDERS.forEach((name) => {
      const shortName = name.split(' ')[0];
      totalByFounder[name] = loans
        .filter((l) => {
          const lender = String(l.values[1] || '');
          return lender === name || lender.startsWith(shortName);
        })
        .filter((l) => !['repaid', 'forgiven'].includes(String(l.values[6] || '').toLowerCase()))
        .reduce((s, l) => {
          const n = parseFloat(String(l.values[2] || '').replace(/[,$]/g, ''));
          return s + (isNaN(n) ? 0 : n);
        }, 0);
    });
  }

  const totalOutstanding = Object.values(totalByFounder).reduce((s, v) => s + v, 0);

  const handleSave = () => {
    setSaving(true); setSaveErr(null);
    // Send interestRate=0 and dueDate='' so backend stays compatible
    fetch('/api/opo/auditor/loans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...draft, interestRate: '0', dueDate: '' }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setShowModal(false); setDraft(EMPTY_LOAN); fetchLoans(); onRefreshSheets && onRefreshSheets(); }
        else setSaveErr(d.error || 'Save failed');
      })
      .catch(e => setSaveErr(e.message))
      .finally(() => setSaving(false));
  };

  const handleDelete = (rowNumber) => {
    if (!window.confirm('Remove this loan entry from the register?')) return;
    setDeleting(rowNumber);
    fetch(`/api/opo/auditor/loans/${rowNumber}`, { method: 'DELETE' })
      .then(() => { fetchLoans(); onRefreshSheets && onRefreshSheets(); })
      .catch(() => {})
      .finally(() => setDeleting(null));
  };

  // Visible columns from XLSX row (skip Interest Rate col 4 and Due Date col 5)
  const displayCols = [0, 1, 2, 3, 6, 7]; // Date, Lender, Amount, Purpose, Status, Notes

  const summary = breakdown?.summary;
  const breakdownByRow = breakdown?.founders
    ? {
      'derived-willem': breakdown.founders.willem,
      'derived-sean': breakdown.founders.sean,
    }
    : {};

  const toggleExpand = (rowNumber, isDerived) => {
    if (!isDerived) return;
    setExpandedRow((prev) => (prev === rowNumber ? null : rowNumber));
  };

  return (
    <div className="loan-pos-panel">

      <div className="loan-pos-overview">
        <div className="loan-pos-overview-head">
          <div>
            <h2 className="loan-pos-overview-title">Shareholder capital &amp; loans</h2>
            <p className="loan-pos-overview-sub">
              {summary?.loanTermsNote || SHAREHOLDER_LOAN_TERMS}. Outstanding balances are bank-truth from{' '}
              <strong>CIBC POSITION</strong> + <strong>BMO POSITION</strong> (cumulative, not fiscal-year scoped).
            </p>
          </div>
          <button
            type="button"
            className="loan-pos-add-btn"
            onClick={() => { setDraft(EMPTY_LOAN); setSaveErr(null); setShowModal(true); }}
          >
            + Record loan
          </button>
        </div>
        <div className="loan-pos-overview-grid">
          <div className="loan-pos-stat loan-pos-stat-equity">
            <div className="loan-pos-stat-label">Sean — share capital (equity)</div>
            <div className="loan-pos-stat-value">{fmtCad(summary?.seanEquity ?? bankSummary?.sean?.angel ?? 0)}</div>
            <div className="loan-pos-stat-hint">Angel investment via numbered company</div>
          </div>
          <div className="loan-pos-stat loan-pos-stat-loan">
            <div className="loan-pos-stat-label">Loans outstanding (founders)</div>
            <div className="loan-pos-stat-value">{fmtCad(summary?.totalLoansOutstanding ?? totalOutstanding)}</div>
            <div className="loan-pos-stat-hint">
              Willem {fmtCad(summary?.willemLoan ?? totalByFounder[FOUNDERS[0]])} outstanding
              {(summary?.shareholderRepaymentsToWillem ?? 0) > 0
                ? ` · ${fmtCad(summary.shareholderRepaymentsToWillem)} repaid to Willem to date`
                : ''}
              {' '}+ Sean {fmtCad(summary?.seanLoan ?? totalByFounder[FOUNDERS[1]])}
            </div>
          </div>
          <div className="loan-pos-stat loan-pos-stat-sean-cash">
            <div className="loan-pos-stat-label">Sean — held in CIBC</div>
            <div className="loan-pos-stat-value">{fmtCad(summary?.seanRetainedInCibc ?? bankSummary?.sean?.loan ?? 0)}</div>
            <div className="loan-pos-stat-hint">Resolution 9 repayment; company custody</div>
          </div>
          <div className="loan-pos-stat loan-pos-stat-cibc">
            <div className="loan-pos-stat-label">CIBC balance / OptiPrime portion</div>
            <div className="loan-pos-stat-value">
              {fmtCad(summary?.cibcBalance ?? bankSummary?.cashHeld ?? 0)}
              <span className="loan-pos-stat-value-sub">
                {' '}· OptiPrime {fmtCad(summary?.cibcOptiprimeCash ?? bankSummary?.cibcOptiprimeCash ?? 0)}
              </span>
            </div>
            <div className="loan-pos-stat-hint">Statement total vs company-attributable cash</div>
          </div>
        </div>
        <p className="loan-pos-table-hint">Click a founder row below to expand the line-by-line calculation.</p>
      </div>

      {/* Loans table */}
      {loading ? (
        <div className="loan-pos-loading">Loading loans…</div>
      ) : loans.length === 0 ? (
        <div className="loan-pos-empty">No loans recorded yet. Use "+ Record loan" to enter a founder loan.</div>
      ) : (
        <div className="loan-pos-table-wrap">
          <table className="loan-pos-table">
            <thead>
              <tr>
                {LOAN_COLS.map(c => <th key={c}>{c}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan) => {
                const isDerived = typeof loan.rowNumber === 'string' && loan.rowNumber.startsWith('derived-');
                const isExpanded = expandedRow === loan.rowNumber;
                const bd = breakdownByRow[loan.rowNumber];
                return (
                  <React.Fragment key={loan.rowNumber}>
                    <tr
                      className={[
                        `loan-row-${String(loan.values[6] || '').toLowerCase().replace(/\s+/g, '-')}`,
                        isDerived ? ' loan-row-derived loan-row-expandable' : '',
                        isExpanded ? ' loan-row-expanded' : '',
                      ].join('')}
                      onClick={() => toggleExpand(loan.rowNumber, isDerived)}
                      role={isDerived ? 'button' : undefined}
                      tabIndex={isDerived ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (isDerived && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          toggleExpand(loan.rowNumber, true);
                        }
                      }}
                    >
                      {displayCols.map((ci) => (
                        <td key={ci} className={ci === 2 ? 'loan-td-num' : ''}>
                          {ci === 1 && isDerived ? (
                            <span className="loan-row-lender">
                              <span className={`loan-row-chevron${isExpanded ? ' loan-row-chevron-open' : ''}`} aria-hidden>▸</span>
                              {String(loan.values[ci] ?? '')}
                            </span>
                          ) : ci === 2 ? fmtCad(loan.values[ci]) : String(loan.values[ci] ?? '')}
                        </td>
                      ))}
                      <td onClick={(e) => e.stopPropagation()}>
                        {isDerived ? (
                          <span className="loan-derived-tag" title="Bank-truth — click row for detail">bank</span>
                        ) : (
                          <button
                            type="button"
                            className="loan-del-btn"
                            onClick={() => handleDelete(loan.rowNumber)}
                            disabled={deleting === loan.rowNumber}
                            title="Remove row"
                          >
                            {deleting === loan.rowNumber ? '…' : '✕'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isDerived && isExpanded && bd && (
                      <tr className="loan-row-detail">
                        <td colSpan={LOAN_COLS.length + 1}>
                          <LoanBreakdownDetail lines={bd.lines} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add loan modal */}
      {showModal && (
        <div className="loan-modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="loan-modal" onClick={e => e.stopPropagation()}>
            <div className="loan-modal-header">
              <span className="loan-modal-title">Record founder loan</span>
              <button className="loan-modal-close" onClick={() => setShowModal(false)} disabled={saving}>✕</button>
            </div>
            <div className="loan-modal-body">
              <div className="loan-form-grid">
                <label>
                  Date
                  <input type="date" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} />
                </label>
                <label>
                  Founder / Lender *
                  <select value={draft.lender} onChange={e => setDraft(d => ({ ...d, lender: e.target.value }))}>
                    {FOUNDERS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
                <label>
                  Amount loaned to company (CAD) *
                  <input
                    type="number" min="0" step="0.01" placeholder="0.00"
                    value={draft.amount}
                    onChange={e => setDraft(d => ({ ...d, amount: e.target.value }))}
                  />
                </label>
                <label>
                  Purpose
                  <input
                    type="text" placeholder="e.g. Operating expenses"
                    value={draft.purpose}
                    onChange={e => setDraft(d => ({ ...d, purpose: e.target.value }))}
                  />
                </label>
                <label>
                  Status
                  <select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}>
                    {LOAN_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="loan-form-notes">
                  Notes
                  <textarea
                    placeholder="Optional notes…"
                    value={draft.notes}
                    onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                    rows={2}
                  />
                </label>
              </div>
              {saveErr && <div className="loan-modal-err">{saveErr}</div>}
            </div>
            <div className="loan-modal-actions">
              <button className="loan-modal-cancel" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button
                className="loan-modal-save"
                onClick={handleSave}
                disabled={saving || !draft.lender || !draft.amount}
              >
                {saving ? 'Saving…' : 'Save to register'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// CashFlowPanel
// ─────────────────────────────────────────────
const ALLOCATION_OPTIONS = [
  'INCOME',
  'OPERATING COSTS','TAX FILING','LICENSES','CBA STARTUP','OPJVC STARTUP','INVESTMENT',
  'TRANSFER TO CIBC','TRANSFER FROM CIBC','SHAREHOLDER LOAN REPAYMENT','CAPITAL ASSET COST','MARKETING COSTS','BANK CHARGES','WEB INFRASTRUCTURE','ASSETS','OTHER',
];
const PAID_BY_OPTIONS = [
  { value: 'FRED',     label: 'Fred (personal loan)' },
  { value: 'SEAN',     label: 'Sean (contribution)' },
  { value: 'CIBC',     label: 'CIBC (company account)' },
  { value: 'TRANSFER', label: 'Internal transfer' },
  { value: 'ASSET',    label: 'Refund / credit' },
  { value: 'OPTIMUM',  label: 'Optimum Realty Inc.' },
];
const PAID_BY_BADGE = {
  FRED:     { label: 'FRED',     cls: 'cf-badge-fred' },
  SEAN:     { label: 'SEAN',     cls: 'cf-badge-sean' },
  CIBC:     { label: 'CIBC',     cls: 'cf-badge-cibc' },
  TRANSFER: { label: 'TRANSFER', cls: 'cf-badge-transfer' },
  ASSET:    { label: 'ASSET',    cls: 'cf-badge-asset' },
  OPTIMUM:  { label: 'OPTIMUM',  cls: 'cf-badge-optimum' },
};
const ALLOC_COLOR = {
  'INCOME':            '#22c55e',
  'OPERATING COSTS':   '#60a5fa',
  'TAX FILING':        '#cbd5e1',
  'LICENSES':          '#86efac',
  'CBA STARTUP':       '#a78bfa',
  'OPJVC STARTUP':     '#c084fc',
  'INVESTMENT':        '#34d399',
  'TRANSFER TO CIBC':  '#fbbf24',
  'TRANSFER FROM CIBC':'#fcd34d',
  'SHAREHOLDER LOAN REPAYMENT': '#38bdf8',
  'CAPITAL ASSET COST':'#f87171',
  'MARKETING COSTS':   '#fb923c',
  'BANK CHARGES':      '#f87171',
  'ASSETS':            '#94a3b8',
};

const EMPTY_CF = { date: new Date().toISOString().slice(0,10), month_code: '', description: '', allocation: 'OPERATING COSTS', amount: '', paid_by: 'FRED', notes: '' };

function CashFlowPanel({ fy = '' }) {
  const [bankSummary, setBankSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
    fetch(`/api/opo/auditor/cashflow-summary${qs}`)
      .then((r) => r.json())
      .then((d) => setBankSummary(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fy]);

  if (loading && !bankSummary) return <div className="cf-loading">Loading cash flow…</div>;

  return (
    <div className="cf-panel">

      {/* Summary cards — bank-truth as at the selected FY (or latest if no FY). */}
      <div className="cf-summary-row">
        <div className="cf-sum-card cf-sum-sean">
          <div className="cf-sum-label">Sean's contributions</div>
          <div className="cf-sum-value">{fmtCad(bankSummary ? bankSummary.sean.total : 0)}</div>
          <div className="cf-sum-sub">
            {bankSummary
              ? `${fmtCad(bankSummary.sean.angel)} share capital · ${fmtCad(bankSummary.sean.heldInCibc ?? bankSummary.sean.bookLoanRepayments ?? 0)} held in CIBC for Sean · ${fmtCad(bankSummary.sean.loan)} still due`
              : '—'}
          </div>
          <div className="cf-sum-source">
            {bankSummary
              ? `${fmtCad(bankSummary.sean.bmoDeposits)} via BMO + ${fmtCad(bankSummary.sean.cibcDirect)} via CIBC. The $1,000 book repayment stays in the company account as Sean's custody cash.`
              : '—'}
          </div>
        </div>
        <div className="cf-sum-card cf-sum-fred">
          <div className="cf-sum-label">Fred's outstanding contribution</div>
          <div className="cf-sum-value">{fmtCad(bankSummary ? (bankSummary.fred.outstandingContribution ?? bankSummary.fred.personalContribution) : 0)}</div>
          <div className="cf-sum-sub">
            {bankSummary
              ? `Advanced ${fmtCad(bankSummary.fred.grossContribution ?? ((bankSummary.fred.outstandingContribution || 0) + (bankSummary.fred.shareholderRepaymentsReceived || 0)))} · cash returned to Fred ${fmtCad(bankSummary.fred.shareholderRepaymentsReceived || 0)}`
              : 'Interest-free shareholder loan still outstanding'}
          </div>
          <div className="cf-sum-source">
            Outstanding = total advanced − CIBC e-transfers back to Fred (four repayments in FY2027, including the $1,700.00).
          </div>
        </div>
        <div className="cf-sum-card cf-sum-col">
          <div className="cf-sum-label">Total external spend</div>
          <div className="cf-sum-value">{fmtCad(bankSummary ? bankSummary.externalSpend : 0)}</div>
          <div className="cf-sum-sub">
            {bankSummary
              ? `Sean ${fmtCad(bankSummary.sean.total)} + Fred outstanding ${fmtCad(bankSummary.fred.outstandingContribution ?? bankSummary.fred.personalContribution)} + refunds/income ${fmtCad(bankSummary.nonShareholderFunding || 0)} − CIBC cash ${fmtCad(bankSummary.cibcBankBalance)}`
              : 'Founder funding + refunds, less cash still on hand'}
          </div>
          <div className="cf-sum-source">
            Bank outflows net of internal transfers and shareholder-loan repayments. Refunds (CRA, Optimum, vendor credits) reduce what founders still have in.
          </div>
        </div>
        <div className="cf-sum-card cf-sum-bank">
          <div className="cf-sum-label">CIBC bank balance</div>
          <div className="cf-sum-value">{fmtCad(bankSummary ? bankSummary.cibcBankBalance : 0)}</div>
          <div className="cf-sum-sub">
            {bankSummary
              ? `${fmtCad(bankSummary.sean.heldInCibc ?? bankSummary.sean.bookLoanRepayments ?? 0)} of this is Sean's held $1,000 · OptiPrime ${fmtCad(bankSummary.cibcOptiprimeCash ?? 0)}`
              : 'Latest CIBC statement closing balance'}
          </div>
          <div className="cf-sum-source">
            {bankSummary?.asAt
              ? `As at ${bankSummary.asAt}`
              : 'Latest CIBC statement closing balance'}
          </div>
        </div>
      </div>

      {/* Read-only banner — Cash Flow is fed by Bank tabs */}
      <div className="cf-readonly-banner">
        <span className="cf-readonly-icon">⟲</span>
        <span>
          The Cash Flow ledger is fed live from <strong>CIBC POSITION</strong> and{' '}
          <strong>BMO_POSITION</strong> (rows ticked "OptiPrime?"). To add or edit entries,
          open those tabs, set the <em>Allocation</em>, <em>Paid by</em>, and <em>CF Description</em>,
          and the rows will appear here automatically.{' '}
          {fy
            ? 'The ledger and the cards above are scoped to the selected fiscal year (cards are as-at year-end / latest bank date in that year).'
            : 'Use the Fiscal year selector above to isolate a single year.'}
        </span>
      </div>

      {/* Ledger table (bank-derived) */}
      <BankDerivedLedger fy={fy} />
    </div>
  );
}

function BankDerivedLedger({ fy = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideInternal, setHideInternal] = useState(false);

  useEffect(() => {
    setLoading(true);
    const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
    fetch(`/api/opo/auditor/cashflow-ledger${qs}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fy]);

  if (loading) return <div className="cf-loading">Loading bank-derived ledger…</div>;
  if (!data || !data.ledger) return <div className="cf-loading">No data.</div>;

  const ledger = hideInternal
    ? data.ledger.filter((r) => !r.isInternal && !(r.paidBy === '' && r.allocation === ''))
    : data.ledger;

  return (
    <div className="cf-table-wrap">
      <div className="cf-ledger-controls">
        <label className="cf-ledger-filter">
          <input type="checkbox" checked={hideInternal} onChange={(e) => setHideInternal(e.target.checked)} />
          Hide internal transfers and corrections
        </label>
        <span className="cf-ledger-count">{ledger.length} entries</span>
      </div>
      <table className="cf-table cf-table-bank">
        <colgroup>
          <col className="cf-col-num" />
          <col className="cf-col-date" />
          <col className="cf-col-source" />
          <col className="cf-col-bank-desc" />
          <col className="cf-col-cf-desc" />
          <col className="cf-col-alloc" />
          <col className="cf-col-paid" />
          <col className="cf-col-money" />
          <col className="cf-col-money" />
          <col className="cf-col-running" />
          <col className="cf-col-running" />
          <col className="cf-col-running" />
        </colgroup>
        <thead>
          <tr>
            <th className="cf-cf-number-head">CF #</th>
            <th>Date</th>
            <th>Source</th>
            <th>Bank description</th>
            <th>CF description</th>
            <th>Allocation</th>
            <th>Paid by</th>
            <th className="cf-num">Wd</th>
            <th className="cf-num">Dep</th>
            <th className="cf-num">Fred running</th>
            <th className="cf-num">Sean running</th>
            <th className="cf-num">External</th>
          </tr>
        </thead>
        <tbody>
          {ledger.map((row) => {
            const badge = PAID_BY_BADGE[row.paidBy] || { label: row.paidBy || '—', cls: '' };
            const allocColor = ALLOC_COLOR[row.allocation] || '#64748b';
            const rowClass = `cf-row cf-row-${(row.paidBy || 'none').toLowerCase()}${row.isInternal ? ' cf-row-internal' : ''}`;
            return (
              <tr key={`${row.source}-${row.sourceRowId}`} className={rowClass}>
                <td className="cf-cf-number">#{row.cfNumber}</td>
                <td className="cf-date">{row.date}</td>
                <td>
                  <span className={`cf-source-badge cf-source-${row.source.toLowerCase()}`}>{row.source}</span>
                </td>
                <td className="cf-desc" title={row.rawLine}>{row.bankDescription}</td>
                <td className="cf-desc cf-cf-desc">{row.cfDescription || (row.isInternal ? <em style={{ color: '#64748b' }}>(internal)</em> : '—')}</td>
                <td>
                  {row.allocation ? (
                    <span className="cf-alloc-badge" style={{ borderColor: allocColor, color: allocColor }}>
                      {row.allocation}
                    </span>
                  ) : <span style={{ color: '#64748b' }}>—</span>}
                </td>
                <td><span className={`cf-paid-badge ${badge.cls}`}>{badge.label}</span></td>
                <td className="cf-num cf-expense">{row.withdrawal > 0 ? fmtCad(row.withdrawal) : '—'}</td>
                <td className="cf-num cf-income">{row.deposit > 0 ? fmtCad(row.deposit) : '—'}</td>
                <td className="cf-num cf-fred-col">{fmtCad(row.fredRunning)}</td>
                <td className="cf-num cf-sean-col">{fmtCad(row.seanRunning)}</td>
                <td className="cf-num cf-col-col">{fmtCad(row.collectiveRunning)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="cf-footer">
            <td colSpan={7}>Totals</td>
            <td className="cf-num cf-expense">{fmtCad(ledger.reduce((s, r) => s + (r.isInternal ? 0 : r.withdrawal), 0))}</td>
            <td className="cf-num cf-income">{fmtCad(ledger.reduce((s, r) => s + (r.isSeanDeposit ? r.deposit : 0), 0))}</td>
            <td className="cf-num cf-fred-col">{fmtCad(data.totals.fredTotal)}</td>
            <td className="cf-num cf-sean-col">{fmtCad(data.totals.seanTotal)}</td>
            <td className="cf-num cf-col-col">{fmtCad(data.totals.externalSpend)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// InvoicingPanel — commercial invoices (01_auditor/invoices/)
// ─────────────────────────────────────────────
function invoicePaymentHref(payment) {
  if (!payment || !payment.sourceRowId || !payment.month_code) return null;
  const tab = payment.source === 'BMO' ? 'BMO_POSITION' : 'BANK_POSITION';
  const qs = new URLSearchParams({
    tab,
    month: payment.month_code,
    tx: String(payment.sourceRowId),
  });
  return `/opo/document/1?${qs.toString()}`;
}

function invoiceInFyWindow(inv, fyWindow) {
  if (!fyWindow) return true;
  const date = String(inv.invoice_date || inv.record?.invoice_date || '').slice(0, 10);
  if (!date) return false;
  if (!fyWindow.openStart && fyWindow.periodFrom && date < fyWindow.periodFrom) return false;
  if (fyWindow.periodTo && date > fyWindow.periodTo) return false;
  return true;
}

function InvoicingPanel({ fy = '', fyWindow = null }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [issuingNumber, setIssuingNumber] = useState(null);
  const [expandedContracts, setExpandedContracts] = useState(() => new Set());

  const loadInvoices = () => fetch('/api/opo/auditor/invoices')
    .then((r) => r.json())
    .then((d) => {
      setInvoices(Array.isArray(d.invoices) ? d.invoices : []);
      setError(null);
    });

  useEffect(() => {
    setLoading(true);
    loadInvoices()
      .catch((e) => setError(e.message || 'Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const issueInvoice = async (invoiceNumber) => {
    setIssuingNumber(invoiceNumber);
    setError(null);
    try {
      const r = await fetch(
        `/api/opo/auditor/invoices/${encodeURIComponent(invoiceNumber)}/issue`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Failed to issue invoice');
      await loadInvoices();
    } catch (e) {
      setError(e.message || 'Failed to issue invoice');
    } finally {
      setIssuingNumber(null);
    }
  };

  if (loading) return <div className="cf-loading">Loading invoices…</div>;
  if (error) return <div className="spreadsheet-viewer-error">{error}</div>;

  const scoped = invoices.filter((inv) => invoiceInFyWindow(inv, fyWindow));
  const isContract = (inv) => (inv.record?.document_type || inv.document_type) === 'contract_statement';
  const isMonthly = (inv) => (inv.record?.document_type || inv.document_type) === 'monthly_invoice'
    || !!(inv.master_invoice || inv.record?.master_invoice_number);
  const statements = scoped.filter((inv) => isContract(inv)
    || (inv.include_in_revenue_total !== false && !isMonthly(inv) && !isContract(inv)));
  const monthly = scoped.filter(isMonthly);
  const monthlyFor = (statementNumber) => monthly
    .filter((inv) => (inv.master_invoice || inv.record?.master_invoice_number) === statementNumber)
    .sort((a, b) => String(a.invoice_date || '').localeCompare(String(b.invoice_date || '')));
  const orphans = monthly.filter((inv) => {
    const master = inv.master_invoice || inv.record?.master_invoice_number;
    return !master || !statements.some((s) => s.invoice_number === master);
  });
  const toggleContract = (number) => {
    setExpandedContracts((prev) => {
      const next = new Set(prev);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  };
  const moneyOf = (val) => {
    const n = typeof val === 'number' ? val : parseFloat(String(val ?? '').replace(/[,$]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  };
  const receivedAgainstStatement = (statementNumber) => monthly
    .filter((inv) => (inv.master_invoice || inv.record?.master_invoice_number) === statementNumber)
    .reduce((sum, inv) => sum + (inv.payment ? moneyOf(inv.payment.amount) : 0), 0);
  const renderRow = (inv, { showIssueButton = false, showPayment = false, showContractBalance = false } = {}) => {
    const amounts = inv.record?.amounts || {};
    const subtotal = amounts.subtotal_excl_tax || inv.subtotal_excl_tax;
    const gst = amounts.gst_amount || inv.gst_amount;
    const total = amounts.total_incl_tax || inv.total_incl_tax;
    const received = showContractBalance ? receivedAgainstStatement(inv.invoice_number) : 0;
    const remaining = showContractBalance ? Math.round((moneyOf(total) - received) * 100) / 100 : null;
    const docType = inv.record?.document_type || inv.document_type || 'invoice';
    const instalment = inv.instalment_number ?? inv.record?.instalment_number;
    const registerStatus = inv.status || 'issued';
    const paid = !!(inv.payment && inv.payment.posted_date);
    const status = paid ? 'paid' : registerStatus;
    const typeLabel = docType === 'contract_statement'
      ? 'Contract statement'
      : docType === 'monthly_invoice'
        ? `Monthly ${instalment != null ? `#${instalment}` : ''}`.trim()
        : 'Invoice';
    const canIssue = showIssueButton && registerStatus === 'scheduled' && !paid;
    return (
      <tr key={inv.invoice_number}>
        <td className="inv-mono">{inv.invoice_number}</td>
        <td>{typeLabel}</td>
        <td>{inv.invoice_date || '—'}</td>
        <td>{inv.customer || inv.record?.customer?.legal_name || '—'}</td>
        <td className="inv-num">{subtotal != null ? fmtCad(subtotal) : '—'}</td>
        <td className="inv-num">{gst != null ? fmtCad(gst) : '—'}</td>
        <td className="inv-num inv-total">{total != null ? fmtCad(total) : '—'}</td>
        {showContractBalance && (
          <>
            <td className="inv-num">{fmtCad(received)}</td>
            <td className="inv-num inv-remaining">{fmtCad(remaining)}</td>
          </>
        )}
        <td><span className={`inv-status inv-status-${status}`}>{status}</span></td>
        <td className="inv-actions">
          {inv.pdfUrl ? (
            <>
              <button
                type="button"
                className="inv-btn"
                onClick={() => {
                  setPreviewUrl(inv.pdfUrl);
                  setPreviewTitle(`${inv.invoice_number} — ${inv.customer || 'Invoice'}`);
                }}
              >
                View PDF
              </button>
              <a
                href={inv.downloadUrl || inv.pdfUrl}
                className="inv-btn inv-btn-link"
                download
              >
                Download
              </a>
              {canIssue && (
                <button
                  type="button"
                  className="inv-btn inv-btn-issue"
                  disabled={issuingNumber === inv.invoice_number}
                  onClick={() => issueInvoice(inv.invoice_number)}
                >
                  {issuingNumber === inv.invoice_number ? 'Issuing…' : 'Issue Invoice'}
                </button>
              )}
            </>
          ) : (
            <span className="inv-muted">PDF missing</span>
          )}
        </td>
        {showPayment && (
          <td className="inv-receipt">
            {paid && invoicePaymentHref(inv.payment) ? (
              <Link
                className="inv-receipt-link"
                to={invoicePaymentHref(inv.payment)}
                title="Open this deposit on CIBC Position"
              >
                {inv.payment.posted_date} · {inv.payment.source}
              </Link>
            ) : (
              <span className="inv-receipt-missing">Payment not there</span>
            )}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="inv-panel">
      <div className="inv-header">
        <h2 className="inv-title">Commercial Invoicing</h2>
        <p className="inv-subtitle">
          Contract statements define scope and total value. Expand a contract to see its monthly tax invoices
          {fyWindow ? ` · ${fyWindow.label} (${fyWindow.rangeLabel})` : ''}. Source:
          {' '}
          <code>OptiPrime AI Inc/01_auditor/invoices/issued/</code>
        </p>
      </div>
      {scoped.length === 0 ? (
        <div className="inv-empty">
          {invoices.length === 0
            ? 'No invoices on file yet.'
            : `No invoices dated in ${fyWindow?.label || fy || 'this fiscal year'}. The Optimum Realty contract (OP-2026-0001) starts 1 Jul 2026 (FY2027).`}
        </div>
      ) : (
        <>
          {statements.map((stmt) => {
            const kids = monthlyFor(stmt.invoice_number);
            const open = expandedContracts.has(stmt.invoice_number);
            return (
              <div key={stmt.invoice_number} className="inv-table-wrap inv-contract-block">
                <button
                  type="button"
                  className="inv-contract-toggle"
                  aria-expanded={open}
                  onClick={() => toggleContract(stmt.invoice_number)}
                >
                  <span className={`inv-row-chevron${open ? ' inv-row-chevron-open' : ''}`} aria-hidden>▸</span>
                  <span className="inv-contract-toggle-label">
                    {stmt.invoice_number} · {stmt.customer || stmt.record?.customer?.legal_name || 'Contract'}
                    {kids.length ? ` · ${kids.length} monthly invoice${kids.length === 1 ? '' : 's'}` : ''}
                    {open ? '' : ' — click to expand'}
                  </span>
                </button>
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th className="inv-num">Subtotal</th>
                      <th className="inv-num">GST</th>
                      <th className="inv-num">Total</th>
                      <th className="inv-num">Received</th>
                      <th className="inv-num">Remaining</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{renderRow(stmt, { showContractBalance: true })}</tbody>
                </table>
                {open && kids.length > 0 && (
                  <div className="inv-nested">
                    <h3 className="inv-section-title">Monthly tax invoices</h3>
                    <p className="inv-section-desc">
                      Send one per month. Use <strong>Issue Invoice</strong> when you send a monthly PDF.
                      Receipt is the CIBC deposit allocated <strong>Income / Optimum Realty Inc.</strong>
                    </p>
                    <table className="inv-table inv-table-nested">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>Instalment</th>
                          <th>Due date</th>
                          <th>Customer</th>
                          <th className="inv-num">Subtotal</th>
                          <th className="inv-num">GST</th>
                          <th className="inv-num">Total</th>
                          <th>Status</th>
                          <th>Actions</th>
                          <th>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>{kids.map((inv) => renderRow(inv, { showIssueButton: true, showPayment: true }))}</tbody>
                    </table>
                  </div>
                )}
                {open && kids.length === 0 && (
                  <p className="inv-section-desc inv-nested-empty">No monthly tax invoices under this contract in this fiscal year.</p>
                )}
              </div>
            );
          })}
          {orphans.length > 0 && (
            <div className="inv-table-wrap">
              <h3 className="inv-section-title">Monthly tax invoices (unassigned)</h3>
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Instalment</th>
                    <th>Due date</th>
                    <th>Customer</th>
                    <th className="inv-num">Subtotal</th>
                    <th className="inv-num">GST</th>
                    <th className="inv-num">Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                    <th>Receipt</th>
                  </tr>
                </thead>
                <tbody>{orphans.map((inv) => renderRow(inv, { showIssueButton: true, showPayment: true }))}</tbody>
              </table>
            </div>
          )}
        </>
      )}
      {previewUrl && (
        <div className="inv-preview-overlay" role="presentation" onClick={() => setPreviewUrl(null)}>
          <div className="inv-preview-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="inv-preview-toolbar">
              <strong>{previewTitle}</strong>
              <div className="inv-preview-actions">
                <a href={previewUrl} className="inv-btn inv-btn-link" target="_blank" rel="noopener noreferrer">Open tab</a>
                <button type="button" className="inv-btn" onClick={() => setPreviewUrl(null)}>Close</button>
              </div>
            </div>
            <iframe title={previewTitle} src={previewUrl} className="inv-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ErrorBoundary — wraps panels to prevent full white-screen crashes
// ─────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px 28px', color: '#f87171', fontFamily: 'monospace' }}>
          <h3 style={{ color: '#fca5a5', marginBottom: 12 }}>Panel Error</h3>
          <pre style={{ fontSize: '0.78rem', background: 'rgba(239,68,68,0.08)', padding: 16, borderRadius: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error ? String(this.state.error) : 'Unknown error'}
          </pre>
          <button style={{ marginTop: 16, padding: '6px 14px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', borderRadius: 6, cursor: 'pointer' }}
            onClick={() => this.setState({ hasError: false, error: null })}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────
// SReDPanel
// ─────────────────────────────────────────────
const CREDIT_RATE = 0.35; // CCPC refundable ITC rate
const SRED_CAT_LABELS = { direct: 'Direct SR&ED', support: 'Supporting', non_eligible: 'Non-Eligible', capital: 'Capital Asset' };
const CRA_FORM = 'T661';

function unwrapSredList(payload, key) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload[key])) return payload[key];
  return [];
}

function SReDPanel({ fy = '' }) {
  const [projects, setProjects]       = useState([]);
  const [classifs, setClassifs]       = useState([]);
  const [expenseTotals, setExpenseTotals] = useState({});
  const [claimMeta, setClaimMeta]     = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeProject, setActiveProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingClassif, setEditingClassif] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [tab, setTab]                 = useState('overview'); // overview | projects | expenses | checklist

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = fy ? `?fy=${encodeURIComponent(fy)}` : '';
      const [pRes, cRes, tRes] = await Promise.all([
        fetch(`/api/opo/auditor/sred/projects${qs}`),
        fetch('/api/opo/auditor/sred/classifications'),
        fetch(`/api/opo/auditor/sred/expense-totals${qs}`),
      ]);
      const [p, c, t] = await Promise.all([pRes.json(), cRes.json(), tRes.json()]);
      const projectList = unwrapSredList(p, 'projects');
      setProjects(projectList);
      setClassifs(unwrapSredList(c, 'classifications'));
      const totals = (t && typeof t === 'object' && !Array.isArray(t)) ? t : {};
      const { _meta, ...byVendor } = totals;
      setExpenseTotals(byVendor);
      setClaimMeta(_meta || null);
      setActiveProject((prev) => {
        if (prev && projectList.some((row) => row.id === prev)) return prev;
        return projectList[0]?.id || null;
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [fy]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Merge bank-truth vendor totals with SR&ED classifications
  const expenseRows = (classifs || []).map(cl => {
    const grossRaw = Number(expenseTotals[cl.description]) || 0;
    const gross = +grossRaw.toFixed(2);
    const eligible = +(gross * (Number(cl.eligible_percent) || 0) / 100).toFixed(2);
    const project = (projects || []).find(p => p.id === cl.sred_project_id);
    return { ...cl, gross, eligible, projectName: project ? project.name : '—' };
  }).filter((r) => r.gross > 0);

  const totalGross    = expenseRows.reduce((s, r) => s + r.gross, 0);
  const totalEligible = expenseRows.reduce((s, r) => s + r.eligible, 0);
  const isAssessed    = String(claimMeta?.status || '').toLowerCase() === 'assessed';
  const refundReceived = Number(claimMeta?.refundReceived || 0);
  const estimatedITC  = isAssessed && refundReceived > 0
    ? refundReceived
    : +(totalEligible * CREDIT_RATE).toFixed(2);
  const directTotal   = expenseRows.filter(r => r.sred_category === 'direct').reduce((s, r) => s + r.eligible, 0);
  const supportTotal  = expenseRows.filter(r => r.sred_category === 'support').reduce((s, r) => s + r.eligible, 0);
  const capitalTotal  = expenseRows.filter(r => r.sred_category === 'capital').reduce((s, r) => s + r.eligible, 0);
  const nonEligibleTracked = expenseRows.filter(r => r.sred_category === 'non_eligible').reduce((s, r) => s + r.eligible, 0);
  const breakdownOther = +(totalEligible - directTotal - supportTotal - capitalTotal - nonEligibleTracked).toFixed(2);

  const pct = (part) => (totalEligible > 0 ? Math.round((part / totalEligible) * 100) : 0);

  const fmt = n => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Save project edits
  const handleSaveProject = async () => {
    setSaving(true);
    const isNew = !editingProject.id;
    const url = isNew ? '/api/opo/auditor/sred/projects' : `/api/opo/auditor/sred/projects/${editingProject.id}`;
    const method = isNew ? 'POST' : 'PUT';
    await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editingProject) });
    setEditingProject(null);
    setShowAddProject(false);
    await fetchAll();
    setSaving(false);
  };

  const handleSaveClassif = async () => {
    setSaving(true);
    await fetch('/api/opo/auditor/sred/classifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingClassif),
    });
    setEditingClassif(null);
    await fetchAll();
    setSaving(false);
  };

  const CHECKLIST = [
    { id: 'c1', cat: 'Financial Records', item: 'List of all SR&ED expenditures with amounts', status: expenseRows.length > 0 ? 'done' : 'missing', note: `${expenseRows.length} expense rows classified` },
    { id: 'c2', cat: 'Financial Records', item: 'Invoices / receipts for each eligible expense', status: 'action', note: 'Upload via Document Management → link to SR&ED claim' },
    { id: 'c3', cat: 'Financial Records', item: 'Bank statements supporting SR&ED payments', status: 'done', note: claimMeta?.latestBankDate ? `Latest statement ${claimMeta.latestBankDate}` : 'Bank statements on file' },
    { id: 'c4', cat: 'Technical Narrative', item: 'Scientific/technological uncertainty description (per project)', status: projects.every(p => p.scientific_uncertainty && p.scientific_uncertainty.length > 50) ? 'done' : 'action', note: `${projects.filter(p => p.scientific_uncertainty && p.scientific_uncertainty.length > 50).length}/${projects.length} projects complete` },
    { id: 'c5', cat: 'Technical Narrative', item: 'Work performed description (hypotheses tested, experiments)', status: projects.every(p => p.work_performed && p.work_performed.length > 50) ? 'done' : 'action', note: `${projects.filter(p => p.work_performed && p.work_performed.length > 50).length}/${projects.length} projects complete` },
    { id: 'c6', cat: 'Technical Narrative', item: 'Technological advancement achieved', status: projects.every(p => p.technological_advancement && p.technological_advancement.length > 50) ? 'done' : 'action', note: `${projects.filter(p => p.technological_advancement && p.technological_advancement.length > 50).length}/${projects.length} projects complete` },
    { id: 'c7', cat: 'CRA Forms', item: `${CRA_FORM} — SR&ED Expenditures Claim`, status: isAssessed ? 'done' : 'pending', note: isAssessed ? 'Filed with the FY2026 T2' : 'File with T2 corporate tax return' },
    { id: 'c8', cat: 'CRA Forms', item: 'T2 Schedule 31 — Investment Tax Credit', status: isAssessed ? 'done' : 'pending', note: isAssessed ? `Federal refund $${(claimMeta?.federalRefund || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : 'Attaches to T2; claims the 35% ITC' },
    { id: 'c9', cat: 'CRA Forms', item: 'T2 Corporate Income Tax Return', status: isAssessed ? 'done' : 'pending', note: isAssessed ? `CRA tax refund $${refundReceived.toLocaleString('en-CA', { minimumFractionDigits: 2 })} received ${claimMeta?.refundDate || ''}` : 'Due 6 months after FYE' },
    { id: 'c10', cat: 'Supporting Docs', item: 'Software version control / commit history (GitHub/git log)', status: 'action', note: 'Demonstrates ongoing R&D activity with timestamped evidence' },
    { id: 'c11', cat: 'Supporting Docs', item: 'API usage logs (Anthropic, OpenAI, Cursor, Hetzner)', status: 'action', note: 'Export usage dashboards as PDFs; attach to claim file' },
    { id: 'c12', cat: 'Supporting Docs', item: 'Proof of CCPC status (share register, minute book)', status: 'done', note: 'Directors & Officers Register + Share Issuance Resolution on file' },
    { id: 'c13', cat: 'Supporting Docs', item: 'T2 filing — previous fiscal years', status: 'done', note: 'First claim; inception Feb 2025, FYE Apr 30, 2026' },
  ];

  const checkDone    = CHECKLIST.filter(c => c.status === 'done').length;
  const checkAction  = CHECKLIST.filter(c => c.status === 'action').length;
  const checkPending = CHECKLIST.filter(c => c.status === 'pending').length;
  const checkPct     = Math.round(checkDone / CHECKLIST.length * 100);

  if (loading) return <div className="sred-loading">Loading SR&amp;ED data…</div>;

  return (
    <div className="sred-panel">
      {/* Header */}
      <div className="sred-header">
        <div className="sred-header-left">
          <h2 className="sred-title">SR&amp;ED Claim Workbook</h2>
          <p className="sred-subtitle">
            Scientific Research &amp; Experimental Development — CRA T661 · OptiPrime AI Inc.
            {claimMeta?.fiscalYear ? ` · ${claimMeta.fiscalYear}` : ''}
            {claimMeta?.asAt ? ` · as at ${claimMeta.asAt}` : ''}
            {isAssessed ? ' · first claim assessed' : ' · working papers (not yet filed)'}
          </p>
        </div>
        <div className="sred-header-right">
          <div className="sred-itc-badge">
            <span className="sred-itc-label">{isAssessed ? 'ITC received' : 'Estimated ITC'}</span>
            <span className="sred-itc-value">{fmt(estimatedITC)}</span>
            <span className="sred-itc-sub">{isAssessed ? `CRA paid ${claimMeta?.refundDate || ''}` : '35% refundable · CCPC (estimate)'}</span>
          </div>
        </div>
      </div>

      {/* Sub-nav */}
      <div className="sred-subnav">
        {[['overview','Overview'],['projects','Projects'],['expenses','Expenses'],['checklist','Checklist']].map(([id, label]) => (
          <button key={id} className={`sred-subnav-btn${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div className="sred-section">
          <div className="sred-summary-cards">
            <div className="sred-card sred-card-blue">
              <span className="sred-card-label">Total R&amp;D Spend</span>
              <span className="sred-card-value">{fmt(totalGross)}</span>
              <span className="sred-card-sub">Operating tech + capital asset (per Expenses tab)</span>
            </div>
            <div className="sred-card sred-card-green">
              <span className="sred-card-label">Total SR&amp;ED Eligible</span>
              <span className="sred-card-value">{fmt(totalEligible)}</span>
              <span className="sred-card-sub">After eligibility adjustments</span>
            </div>
            <div className="sred-card sred-card-gold">
              <span className="sred-card-label">{isAssessed ? 'ITC refund received' : 'Estimated ITC refund'}</span>
              <span className="sred-card-value">{fmt(estimatedITC)}</span>
              <span className="sred-card-sub">
                {isAssessed
                  ? `Federal ${fmt(claimMeta?.federalRefund)} + BC ${fmt(claimMeta?.bcRefund)}`
                  : '35% × current eligible · CCPC estimate — not filed'}
              </span>
            </div>
            <div className="sred-card sred-card-purple">
              <span className="sred-card-label">Claim Readiness</span>
              <span className="sred-card-value">{checkPct}%</span>
              <span className="sred-card-sub">{checkDone} done · {checkAction} action · {checkPending} pending</span>
            </div>
          </div>

          <div className="sred-overview-grid">
            <div className="sred-overview-block">
              <h4>Expense Breakdown</h4>
              <p className="sred-breakdown-hint">Amounts sum to Total Eligible. DGX Spark is shown under Capital Asset (s.37 vs CCA — see Expenses tab).</p>
              <table className="sred-mini-table">
                <thead><tr><th>Category</th><th>Amount</th><th>%</th></tr></thead>
                <tbody>
                  <tr><td><span className="sred-cat-badge direct">Direct SR&amp;ED</span></td><td className="privacy-amt">{fmt(directTotal)}</td><td>{pct(directTotal)}%</td></tr>
                  <tr><td><span className="sred-cat-badge support">Supporting</span></td><td className="privacy-amt">{fmt(supportTotal)}</td><td>{pct(supportTotal)}%</td></tr>
                  <tr><td><span className="sred-cat-badge capital">Capital asset</span></td><td className="privacy-amt">{fmt(capitalTotal)}</td><td>{pct(capitalTotal)}%</td></tr>
                  {nonEligibleTracked > 0 && (
                    <tr><td><span className="sred-cat-badge non_eligible">Non-eligible (tracked)</span></td><td className="privacy-amt">{fmt(nonEligibleTracked)}</td><td>{pct(nonEligibleTracked)}%</td></tr>
                  )}
                  {Math.abs(breakdownOther) > 0.01 && (
                    <tr><td><span className="sred-cat-badge support">Other categories</span></td><td className="privacy-amt">{fmt(breakdownOther)}</td><td>{pct(breakdownOther)}%</td></tr>
                  )}
                  <tr className="sred-total-row"><td>Total Eligible</td><td className="privacy-amt">{fmt(totalEligible)}</td><td>100%</td></tr>
                </tbody>
              </table>
            </div>

            <div className="sred-overview-block">
              <h4>Credit Calculation</h4>
              <table className="sred-mini-table">
                <tbody>
                  <tr><td>Current SR&amp;ED (T661 L380)</td><td className="privacy-amt">{fmt(claimMeta?.currentExpenditures ?? expenseRows.filter((r) => r.sred_category !== 'capital').reduce((s, r) => s + r.eligible, 0))}</td></tr>
                  <tr><td>Capital SR&amp;ED (DGX Spark)</td><td className="privacy-amt">{fmt(claimMeta?.capitalExpenditures ?? capitalTotal)}</td></tr>
                  <tr><td>ITC rate (CCPC, current)</td><td>35%</td></tr>
                  <tr className="sred-total-row"><td>{isAssessed ? 'CRA refund received' : 'Estimated ITC refund'}</td><td className="privacy-amt">{fmt(estimatedITC)}</td></tr>
                  {isAssessed && <tr><td>Refund date</td><td>{claimMeta.refundDate}</td></tr>}
                  <tr><td>Applicable form</td><td>{CRA_FORM} + Sch.31 + T666</td></tr>
                  <tr><td>Bank coverage</td><td>{claimMeta?.latestBankDate || '—'}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="sred-overview-block">
              <h4>Projects in Claim</h4>
              {projects.map(p => (
                <div key={p.id} className="sred-project-chip">
                  <strong>{p.name}</strong>
                  <span className={`sred-status-badge ${p.status}`}>{p.status}</span>
                  <div className="sred-project-chip-period">{p.claim_period_start} → {p.claim_period_end}</div>
                </div>
              ))}
            </div>

            <div className="sred-overview-block">
              <h4>Important Notes</h4>
              <ul className="sred-notes-list">
                <li>No salaries paid — claim is entirely third-party material / API costs</li>
                <li>{isAssessed ? 'FY2026 first claim is assessed — CRA paid $2,707.00 on 2026-08-04.' : 'FY2027 working papers only — T661 not yet filed. Totals follow bank statements received.'}</li>
                <li>CCPC status confirmed — 35% refundable ITC on current expenditures</li>
                <li>Twilio classified as direct SR&amp;ED (product use, OptiLaw)</li>
                <li>Hetzner 100% eligible — all compute used for R&amp;D</li>
                <li>Web infrastructure (Porkbun) is <strong>not</strong> SR&amp;ED eligible</li>
                <li>DGX Spark included — ITA s.37 election option vs CCA. <strong>Discuss with CPA.</strong></li>
                <li>Marketing costs (Web Summit) are <strong>not</strong> SR&amp;ED eligible</li>
                <li>Bank charges are <strong>not</strong> SR&amp;ED eligible</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ── PROJECTS ── */}
      {tab === 'projects' && (
        <div className="sred-section">
          <div className="sred-section-header">
            <h3>SR&amp;ED Projects</h3>
            <button className="sred-btn-primary" onClick={() => { setEditingProject({ name:'', fiscal_year_end:'2026-04-30', claim_period_start:'2025-02-10', claim_period_end:'2026-03-31', scientific_uncertainty:'', work_performed:'', technological_advancement:'', status:'draft', notes:'' }); setShowAddProject(true); }}>+ Add Project</button>
          </div>
          <p className="sred-section-hint">Each project requires a technical narrative on CRA Form T661. Be specific — CRA reviewers look for scientific/technological uncertainty, not business uncertainty.</p>

          <div className="sred-project-tabs">
            {projects.map(p => (
              <button key={p.id} className={`sred-project-tab-btn${activeProject === p.id ? ' active' : ''}`} onClick={() => setActiveProject(p.id)}>
                {p.name}
                <span className={`sred-status-badge ${p.status}`}>{p.status}</span>
              </button>
            ))}
          </div>

          {projects.filter(p => p.id === activeProject).map(p => (
            <div key={p.id} className="sred-project-detail">
              <div className="sred-project-detail-header">
                <div>
                  <span className="sred-project-period">Claim period: {p.claim_period_start} → {p.claim_period_end}</span>
                  <span className="sred-project-fye">  FYE: {p.fiscal_year_end}</span>
                </div>
                <button className="sred-btn-secondary" onClick={() => setEditingProject({ ...p })}>Edit</button>
              </div>

              <div className="sred-narrative-block">
                <div className="sred-narrative-label">
                  <span className="sred-t661-ref">T661 Part 2 — Line 242</span>
                  Scientific/Technological Uncertainty
                </div>
                <div className="sred-narrative-text">{p.scientific_uncertainty || <em className="sred-empty">Not yet entered</em>}</div>
              </div>

              <div className="sred-narrative-block">
                <div className="sred-narrative-label">
                  <span className="sred-t661-ref">T661 Part 2 — Line 244</span>
                  Work Performed
                </div>
                <div className="sred-narrative-text">{p.work_performed || <em className="sred-empty">Not yet entered</em>}</div>
              </div>

              <div className="sred-narrative-block">
                <div className="sred-narrative-label">
                  <span className="sred-t661-ref">T661 Part 2 — Line 246</span>
                  Technological Advancement
                </div>
                <div className="sred-narrative-text">{p.technological_advancement || <em className="sred-empty">Not yet entered</em>}</div>
              </div>

              {p.notes && (
                <div className="sred-narrative-block sred-notes-block">
                  <div className="sred-narrative-label">Internal Notes</div>
                  <div className="sred-narrative-text">{p.notes}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── EXPENSES ── */}
      {tab === 'expenses' && (
        <div className="sred-section">
          <div className="sred-section-header">
            <h3>Expense Classifications</h3>
            <button className="sred-btn-secondary" onClick={() => setEditingClassif({ description:'', sred_category:'direct', eligible_percent:100, sred_project_id: projects[0]?.id || 1, cra_line:'', notes:'' })}>+ Add Row</button>
          </div>
          <p className="sred-section-hint">Amounts are pulled automatically from the Cash Flow ledger. Edit the classification or eligible % to adjust SR&amp;ED eligibility.</p>

          <table className="sred-expense-table">
            <thead>
              <tr>
                <th>Vendor / Description</th>
                <th>Project</th>
                <th>Category</th>
                <th>Eligible %</th>
                <th className="num">Gross Spend</th>
                <th className="num">SR&amp;ED Eligible</th>
                <th>CRA Line Ref</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {expenseRows.map(row => (
                <tr key={row.id}>
                  <td className="sred-vendor-cell"><strong>{row.description}</strong></td>
                  <td className="sred-project-cell">{row.projectName}</td>
                  <td><span className={`sred-cat-badge ${row.sred_category}`}>{SRED_CAT_LABELS[row.sred_category] || row.sred_category}</span></td>
                  <td className="num">{row.eligible_percent}%</td>
                  <td className="num">{fmt(row.gross)}</td>
                  <td className="num sred-eligible-amt">{fmt(row.eligible)}</td>
                  <td className="sred-cra-ref">{row.cra_line || '—'}</td>
                  <td className="sred-notes-cell">{row.notes || '—'}</td>
                  <td><button className="sred-edit-btn" onClick={() => setEditingClassif({ ...row })}>Edit</button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="sred-expense-total">
                <td colSpan={4}><strong>TOTALS</strong></td>
                <td className="num"><strong>{fmt(totalGross)}</strong></td>
                <td className="num sred-eligible-amt"><strong>{fmt(totalEligible)}</strong></td>
                <td colSpan={3}><strong>Estimated ITC @ 35%: {fmt(estimatedITC)}</strong></td>
              </tr>
            </tfoot>
          </table>

          <div className="sred-expense-note">
            <strong>⚠️ DGX Spark SR&amp;ED Election — Important:</strong> The DGX Spark is included above as a SR&amp;ED line ({fmt((expenseTotals['DGX Spark (Capital Asset)'] || 0))}). You have two options — choose ONE with your CPA before filing:
            <br /><br />
            <strong>Option A — ITA s.37(1)(b) Full Expensing:</strong> Elect to expense the full capital cost in the year of acquisition as a SR&amp;ED expenditure. Maximizes the current-year ITC refund. The asset is then fully deducted and not eligible for future CCA.
            <br /><br />
            <strong>Option B — CCA Class 50 (55%/year):</strong> Capitalize and depreciate normally, no SR&amp;ED expenditure claim. The asset stays on the balance sheet as per the P&amp;L. Lower immediate cash benefit but retains the asset value on books.
            <br /><br />
            <strong>Do NOT claim the DGX Spark in both the SR&amp;ED expenditures AND CCA.</strong>
          </div>
        </div>
      )}

      {/* ── CHECKLIST ── */}
      {tab === 'checklist' && (
        <div className="sred-section">
          <div className="sred-section-header">
            <h3>Filing Readiness Checklist</h3>
            <div className="sred-progress-bar-wrap">
              <div className="sred-progress-bar" style={{ width: checkPct + '%' }} />
              <span className="sred-progress-label">{checkPct}% ready</span>
            </div>
          </div>
          <p className="sred-section-hint">Complete all <span className="sred-badge-action">Action Required</span> items before submitting your T661. <span className="sred-badge-pending">Pending</span> items are completed at filing time.</p>

          {['Financial Records','Technical Narrative','CRA Forms','Supporting Docs'].map(cat => (
            <div key={cat} className="sred-checklist-group">
              <h4 className="sred-checklist-cat">{cat}</h4>
              {CHECKLIST.filter(c => c.cat === cat).map(c => (
                <div key={c.id} className={`sred-checklist-item sred-chk-${c.status}`}>
                  <span className="sred-chk-icon">
                    {c.status === 'done' ? '✅' : c.status === 'action' ? '⚠️' : '🕐'}
                  </span>
                  <div className="sred-chk-body">
                    <div className="sred-chk-title">{c.item}</div>
                    <div className="sred-chk-note">{c.note}</div>
                  </div>
                  <span className={`sred-badge-${c.status}`}>
                    {c.status === 'done' ? 'Complete' : c.status === 'action' ? 'Action Required' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          ))}

          <div className="sred-filing-tip">
            <strong>Filing deadline:</strong> SR&amp;ED claims must be filed within 18 months of your FYE. FYE Apr 30, 2026 → deadline <strong>Oct 31, 2027</strong>. However, we recommend filing with the T2 by <strong>Oct 31, 2026</strong>.
          </div>
        </div>
      )}

      {/* ── EDIT PROJECT MODAL ── */}
      {editingProject && (
        <div className="sred-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) { setEditingProject(null); setShowAddProject(false); } }}>
          <div className="sred-modal">
            <h3 className="sred-modal-title">{showAddProject ? 'Add SR&ED Project' : 'Edit Project'}</h3>
            <div className="sred-modal-field">
              <label>Project Name</label>
              <input className="sred-input" value={editingProject.name || ''} onChange={e => setEditingProject(p => ({...p, name: e.target.value}))} />
            </div>
            <div className="sred-modal-row">
              <div className="sred-modal-field">
                <label>Claim Period Start</label>
                <input type="date" className="sred-input" value={editingProject.claim_period_start || ''} onChange={e => setEditingProject(p => ({...p, claim_period_start: e.target.value}))} />
              </div>
              <div className="sred-modal-field">
                <label>Claim Period End</label>
                <input type="date" className="sred-input" value={editingProject.claim_period_end || ''} onChange={e => setEditingProject(p => ({...p, claim_period_end: e.target.value}))} />
              </div>
              <div className="sred-modal-field">
                <label>Status</label>
                <select className="sred-input" value={editingProject.status || 'draft'} onChange={e => setEditingProject(p => ({...p, status: e.target.value}))}>
                  <option value="draft">Draft</option>
                  <option value="ready">Ready</option>
                  <option value="filed">Filed</option>
                </select>
              </div>
            </div>
            <div className="sred-modal-field">
              <label>Scientific / Technological Uncertainty <span className="sred-t661-ref">T661 Line 242</span></label>
              <textarea className="sred-textarea" rows={5} value={editingProject.scientific_uncertainty || ''} onChange={e => setEditingProject(p => ({...p, scientific_uncertainty: e.target.value}))} placeholder="Describe the specific technological uncertainty that existed at the start of the work — what was NOT known and could not be deduced by standard practice?" />
            </div>
            <div className="sred-modal-field">
              <label>Work Performed <span className="sred-t661-ref">T661 Line 244</span></label>
              <textarea className="sred-textarea" rows={5} value={editingProject.work_performed || ''} onChange={e => setEditingProject(p => ({...p, work_performed: e.target.value}))} placeholder="Describe the systematic investigation or search — hypotheses formed, experiments designed, data collected, results analyzed." />
            </div>
            <div className="sred-modal-field">
              <label>Technological Advancement <span className="sred-t661-ref">T661 Line 246</span></label>
              <textarea className="sred-textarea" rows={4} value={editingProject.technological_advancement || ''} onChange={e => setEditingProject(p => ({...p, technological_advancement: e.target.value}))} placeholder="What new knowledge or capability was gained? What can now be done that couldn't be done before?" />
            </div>
            <div className="sred-modal-field">
              <label>Internal Notes</label>
              <textarea className="sred-textarea" rows={2} value={editingProject.notes || ''} onChange={e => setEditingProject(p => ({...p, notes: e.target.value}))} />
            </div>
            <div className="sred-modal-actions">
              <button className="sred-btn-secondary" onClick={() => { setEditingProject(null); setShowAddProject(false); }}>Cancel</button>
              <button className="sred-btn-primary" disabled={saving} onClick={handleSaveProject}>{saving ? 'Saving…' : 'Save Project'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT CLASSIFICATION MODAL ── */}
      {editingClassif && (
        <div className="sred-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setEditingClassif(null); }}>
          <div className="sred-modal">
            <h3 className="sred-modal-title">Edit Expense Classification</h3>
            <div className="sred-modal-field">
              <label>Vendor / Description</label>
              <input className="sred-input" value={editingClassif.description || ''} onChange={e => setEditingClassif(c => ({...c, description: e.target.value}))} />
            </div>
            <div className="sred-modal-row">
              <div className="sred-modal-field">
                <label>SR&amp;ED Category</label>
                <select className="sred-input" value={editingClassif.sred_category || 'direct'} onChange={e => setEditingClassif(c => ({...c, sred_category: e.target.value}))}>
                  <option value="direct">Direct SR&amp;ED</option>
                  <option value="support">Supporting</option>
                  <option value="non_eligible">Non-Eligible</option>
                </select>
              </div>
              <div className="sred-modal-field">
                <label>Eligible %</label>
                <input type="number" min="0" max="100" className="sred-input" value={editingClassif.eligible_percent ?? 100} onChange={e => setEditingClassif(c => ({...c, eligible_percent: +e.target.value}))} />
              </div>
              <div className="sred-modal-field">
                <label>SR&amp;ED Project</label>
                <select className="sred-input" value={editingClassif.sred_project_id || 1} onChange={e => setEditingClassif(c => ({...c, sred_project_id: +e.target.value}))}>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="sred-modal-field">
              <label>CRA Line Reference</label>
              <input className="sred-input" value={editingClassif.cra_line || ''} onChange={e => setEditingClassif(c => ({...c, cra_line: e.target.value}))} placeholder="e.g. SR&ED expenditure – materials/third-party payments" />
            </div>
            <div className="sred-modal-field">
              <label>Notes</label>
              <textarea className="sred-textarea" rows={3} value={editingClassif.notes || ''} onChange={e => setEditingClassif(c => ({...c, notes: e.target.value}))} />
            </div>
            <div className="sred-modal-actions">
              <button className="sred-btn-secondary" onClick={() => setEditingClassif(null)}>Cancel</button>
              <button className="sred-btn-primary" disabled={saving} onClick={handleSaveClassif}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Must match server linkedDocumentViewerForPath — used if /meta omits viewer (legacy). */
function viewerFromFilePath(filePath) {
  const fp = String(filePath || '').toLowerCase();
  const ext = fp.includes('.') ? fp.slice(fp.lastIndexOf('.')) : '';
  if (['.xlsx', '.xls', '.xlsm', '.xlsb'].includes(ext)) return 'spreadsheet';
  if (ext === '.pdf') return 'pdf';
  if (['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(ext)) return 'image';
  if (['.txt', '.md', '.markdown', '.csv', '.json', '.xml', '.log'].includes(ext)) return 'text';
  return 'binary';
}

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────
function SpreadsheetViewerPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || '';
  const [meta, setMeta]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [data, setData]             = useState(null);
  const [textContent, setTextContent] = useState('');
  const [activeSheet, setActiveSheet] = useState('');
  const [sheetSyncNotice, setSheetSyncNotice] = useState(null);
  const [workbookRefreshing, setWorkbookRefreshing] = useState(false);
  // Fiscal-year scope for the whole document (FYE April 30). Default is the
  // year we are actually in (FY2027 as of Sep 2026), not the first short year.
  const [fiscalYears, setFiscalYears] = useState([]);
  const [fiscalYear, setFiscalYear] = useState(() => currentFiscalYearId());
  const fiscalYearRef = useRef(fiscalYear);
  useEffect(() => { fiscalYearRef.current = fiscalYear; }, [fiscalYear]);
  const [privacyOn, setPrivacyOn] = useState(() => {
    try { return localStorage.getItem('opo-spreadsheet-privacy') === '1'; } catch (_) { return false; }
  });
  const togglePrivacy = useCallback(() => {
    setPrivacyOn((prev) => {
      const next = !prev;
      try { localStorage.setItem('opo-spreadsheet-privacy', next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const fileUrl = documentId ? `/api/opo/auditor/linked-documents/${documentId}/file` : '';

  useEffect(() => {
    if (documentId !== '1') return;
    fetch('/api/opo/auditor/fiscal-years')
      .then((r) => r.json())
      .then((d) => {
        const all = (d && d.fiscalYears) || [];
        const visible = (d && d.visibleFiscalYears && d.visibleFiscalYears.length)
          ? d.visibleFiscalYears
          : visibleFiscalYears(all, (d && d.currentId) || currentFiscalYearId());
        setFiscalYears(visible.length ? visible : all);
      })
      .catch(() => {});
  }, [documentId]);

  const fetchSheets = useCallback(
    (silent = false, refresh = false) => {
      if (!documentId) return Promise.resolve();
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      if (refresh) params.set('refresh', '1');
      if (fiscalYearRef.current) params.set('fy', fiscalYearRef.current);
      const q = params.toString() ? `?${params.toString()}` : '';
      return fetch(`/api/opo/auditor/subagent/accountant/documents/${documentId}/sheets${q}`)
        .then((r) => r.text().then((text) => ({ ok: r.ok, status: r.status, text })))
        .then(({ ok, status, text }) => {
          const trimmed = (text || '').trim();
          if (trimmed.startsWith('<')) throw new Error('Server returned a page instead of data.');
          let d;
          try { d = JSON.parse(text); } catch (_) { throw new Error('Invalid response from server.'); }
          if (!ok && d.error) throw new Error(d.error);
          if (!ok) throw new Error(`Request failed (${status}).`);
          setData(d);
          setActiveSheet((prev) => {
            const names = d.sheetNames || [];
            const virtual = documentId === '1' ? ['BMO_POSITION', 'INVOICING'] : [];
            if (initialTab && (names.includes(initialTab) || virtual.includes(initialTab))) return initialTab;
            if (prev && (names.includes(prev) || virtual.includes(prev))) return prev;
            return names[0] || '';
          });
        })
        .finally(() => { if (!silent) setLoading(false); });
    },
    [documentId, initialTab]
  );

  useEffect(() => {
    if (documentId === '1' && initialTab) setActiveSheet(initialTab);
  }, [documentId, initialTab]);

  // Regenerate every statement tab when the fiscal-year scope changes.
  const didMountFyRef = useRef(false);
  useEffect(() => {
    if (!didMountFyRef.current) { didMountFyRef.current = true; return; }
    if (meta && meta.viewer === 'spreadsheet') fetchSheets(true);
  }, [fiscalYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!documentId) { setError('No document specified'); setLoading(false); return; }
    setError(null); setMeta(null); setData(null); setTextContent(''); setLoading(true);

    fetch(`/api/opo/auditor/linked-documents/${documentId}/meta`)
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j.error || 'Failed to load document');
        if (!j.exists) throw new Error('File not found on server');
        const viewer = j.viewer || viewerFromFilePath(j.filePath);
        setMeta({ ...j, viewer });
        if (viewer === 'spreadsheet') return fetchSheets(false);
        if (viewer === 'text') {
          return fetch(`/api/opo/auditor/linked-documents/${documentId}/file`)
            .then((r) => { if (!r.ok) throw new Error('Failed to load file'); return r.text(); })
            .then((t) => { setTextContent(t); setLoading(false); });
        }
        setLoading(false);
      })
      .catch((e) => { setError(e.message || 'Failed to load document'); setLoading(false); });
  }, [documentId, fetchSheets]);

  const handleClose = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    window.close();
    queueMicrotask(() => {
      if (document.visibilityState !== 'visible') return;
      if (window.history.length > 1) navigate(-1);
      else navigate('/opo');
    });
  };

  if (loading) return <div className="spreadsheet-viewer-page"><div className="spreadsheet-viewer-loading">Loading document…</div></div>;

  if (error || !meta) {
    return (
      <div className="spreadsheet-viewer-page">
        <div className="spreadsheet-viewer-toolbar">
          <button type="button" className="spreadsheet-viewer-close" onClick={handleClose}>Close</button>
        </div>
        <div className="spreadsheet-viewer-error">{error || 'Document not found'}</div>
      </div>
    );
  }

  const toolbar = (
    <div className="spreadsheet-viewer-toolbar">
      <h1 className="spreadsheet-viewer-title">{meta.label || 'Document'}</h1>
      <div className="spreadsheet-viewer-toolbar-actions">
        <a href={fileUrl} download className="spreadsheet-viewer-download" target="_blank" rel="noopener noreferrer">Download</a>
        <button type="button" className="spreadsheet-viewer-close" onClick={handleClose}>Close</button>
      </div>
    </div>
  );

  if (meta.viewer === 'pdf') return (
    <div className="spreadsheet-viewer-page spreadsheet-viewer-page--embed">{toolbar}<iframe title={meta.label || 'PDF'} className="spreadsheet-viewer-iframe" src={fileUrl} /></div>
  );
  if (meta.viewer === 'image') return (
    <div className="spreadsheet-viewer-page spreadsheet-viewer-page--embed">{toolbar}<div className="spreadsheet-viewer-image-wrap"><img src={fileUrl} alt={meta.label || ''} className="spreadsheet-viewer-image" /></div></div>
  );
  if (meta.viewer === 'text') return (
    <div className="spreadsheet-viewer-page">{toolbar}<pre className="spreadsheet-viewer-text">{textContent}</pre></div>
  );
  if (meta.viewer === 'binary') return (
    <div className="spreadsheet-viewer-page">{toolbar}<div className="spreadsheet-viewer-binary-hint"><p>This file type is not shown inline.</p><p><a href={fileUrl} className="spreadsheet-viewer-download-link" target="_blank" rel="noopener noreferrer">Open or download file</a></p></div></div>
  );

  if (meta.viewer === 'spreadsheet') {
    if (!data) return (
      <div className="spreadsheet-viewer-page">{toolbar}<div className="spreadsheet-viewer-error">Could not load spreadsheet data</div></div>
    );

    // Inject BMO_POSITION as a virtual tab on document 1 (alongside BANK_POSITION).
    // The tab is fully client-side; data lives in the bmo_statement_transactions table
    // and on disk under expenses/bmo_personal_statements/, not in the workbook.
    const rawSheetNames = data.sheetNames || [];
    const sheetNames = (() => {
      if (documentId !== '1') return rawSheetNames;
      let names = rawSheetNames;
      if (!names.includes('BMO_POSITION')) {
        const idx = names.indexOf('BANK_POSITION');
        if (idx >= 0) {
          names = [...names.slice(0, idx + 1), 'BMO_POSITION', ...names.slice(idx + 1)];
        }
      }
      if (!names.includes('INVOICING')) {
        names = [...names, 'INVOICING'];
      }
      return names;
    })();
    const sheets     = data.sheets    || {};
    let rawRows      = sheets[activeSheet] || [];
    if (SCHEDULE_SHEETS.includes(activeSheet)) {
      rawRows = rawRows.filter(spreadsheetRowHasContent);
      let lastUsed = 0;
      for (const r of rawRows) {
        if (!Array.isArray(r)) continue;
        for (let i = r.length - 1; i >= 0; i -= 1) {
          const cell = r[i];
          if (cell !== '' && cell != null) {
            lastUsed = Math.max(lastUsed, i + 1);
            break;
          }
        }
      }
      const cap = Math.max(lastUsed, 2);
      rawRows = rawRows.map((r) => (Array.isArray(r) ? r.slice(0, cap) : r));
    }
    const colCount   = Math.max(0, ...rawRows.map((r) => (Array.isArray(r) ? r.length : 0)));
    const rows       = rawRows.map((r) => {
      const row = Array.isArray(r) ? [...r] : [];
      while (row.length < colCount) row.push('');
      return row;
    });

    const FS_TABS       = ['Statement of Operations','Balance Sheet','Changes in Equity','Cash Flows — ASPE','Notes'];
    const SCHEDULE_TABS = SCHEDULE_SHEETS;
    const WORKING_TABS  = ['BANK_POSITION','BMO_POSITION','LOAN_POSITION','CASH_FLOW','SR&ED','INVOICING'];
    const formatNavLabel = (name) => String(name || '').replace(/_/g, ' ').toUpperCase();

    const TAB_GROUPS = [
      { id: 'fs', label: 'FINANCIAL STATEMENTS', names: FS_TABS },
      { id: 'schedules', label: 'SCHEDULES', names: SCHEDULE_TABS },
      { id: 'working', label: 'WORKING PAPERS', names: WORKING_TABS },
    ];
    const groupedNames = new Set(TAB_GROUPS.flatMap((g) => g.names));
    const leftoverNames = sheetNames.filter((n) => !groupedNames.has(n));
    const navGroups = [
      ...TAB_GROUPS.map((g) => ({ ...g, names: g.names.filter((n) => sheetNames.includes(n)) }))
        .filter((g) => g.names.length > 0),
      ...(leftoverNames.length ? [{ id: 'other', label: 'OTHER', names: leftoverNames }] : []),
    ];
    const activeFy = fiscalYears.find((y) => y.id === fiscalYear) || null;
    const tabIcon = (name) => ({
      'Statement of Operations': '📊',
      'Balance Sheet': '⚖️',
      'Changes in Equity': '📈',
      'Cash Flows — ASPE': '💵',
      'Notes': '📝',
      CAPITAL_ASSETS: '🖥️',
      STARTUP_COSTS: '🏢',
      OPERATING_COSTS: '⚙️',
      BANK_POSITION: '🏦',
      BMO_POSITION: '💳',
      LOAN_POSITION: '🤝',
      CASH_FLOW: '💰',
      'SR&ED': '🔬',
      INVOICING: '🧾',
    }[name] || '');
    const tabLabel = (name) => WORKBOOK_TAB_LABELS[name] || formatNavLabel(name);
    const openSheet = (name) => {
      setActiveSheet(name);
      if (documentId === '1') {
        navigate(`/opo/document/1?tab=${encodeURIComponent(name)}`, { replace: true });
      }
    };

    return (
      <div className={`spreadsheet-viewer-page${privacyOn ? ' spreadsheet-viewer-page--privacy' : ''}`}>
        {/* Toolbar */}
        <div className="spreadsheet-viewer-toolbar">
          <h1 className="spreadsheet-viewer-title">{documentId === '1' ? 'ASPE Compliant Preparation Record' : (data.label || meta.label || 'Spreadsheet')}</h1>
          {documentId === '1' && fiscalYears.length > 0 && (
            <div className="spreadsheet-viewer-fy-toolbar" role="group" aria-label="Fiscal year">
              <span className="spreadsheet-viewer-fy-toolbar-label">Fiscal year</span>
              <div className="spreadsheet-viewer-fy-pills">
                {fiscalYears.map((y) => {
                  const isActive = fiscalYear === y.id;
                  return (
                    <button
                      key={y.id}
                      type="button"
                      className={`spreadsheet-viewer-fy-pill${isActive ? ' spreadsheet-viewer-fy-pill-active' : ''}`}
                      title={`${y.rangeLabel}, FYE ${y.periodTo}${isActive ? ' — currently selected' : ''}`}
                      aria-pressed={isActive}
                      onClick={() => setFiscalYear(y.id)}
                    >
                      <span className="spreadsheet-viewer-fy-pill-id">{y.label}</span>
                      <span className="spreadsheet-viewer-fy-pill-range">{y.rangeLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="spreadsheet-viewer-toolbar-actions">
            <button
              type="button"
              className={`spreadsheet-viewer-privacy${privacyOn ? ' spreadsheet-viewer-privacy-active' : ''}`}
              aria-pressed={privacyOn}
              aria-label={privacyOn ? 'Show financial amounts' : 'Hide financial amounts'}
              title={privacyOn ? 'Privacy on — click to show amounts' : 'Privacy — hide financial amounts'}
              onClick={togglePrivacy}
            >
              <i className={`fas ${privacyOn ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
              <span>Privacy</span>
            </button>
            <button
              type="button"
              className="spreadsheet-viewer-download"
              disabled={workbookRefreshing}
              title="Re-read expense PDFs and rewrite OPERATING_COSTS from the database"
              onClick={() => {
                setSheetSyncNotice(null);
                setWorkbookRefreshing(true);
                fetchSheets(true, true)
                  .then(() => {
                    setSheetSyncNotice({
                      ok: true,
                      text: 'Operating costs synced from linked expense documents (and saved to the workbook file).',
                    });
                  })
                  .catch((e) => {
                    setSheetSyncNotice({ ok: false, text: e.message || 'Refresh failed.' });
                  })
                  .finally(() => setWorkbookRefreshing(false));
              }}
            >
              {workbookRefreshing ? 'Refreshing…' : 'Refresh workbook'}
            </button>
            <a href={fileUrl} download className="spreadsheet-viewer-download" target="_blank" rel="noopener noreferrer">Download</a>
            <button type="button" className="spreadsheet-viewer-close" onClick={handleClose}>Close</button>
          </div>
        </div>
        {sheetSyncNotice && (
          <div className={sheetSyncNotice.ok ? 'spreadsheet-viewer-sync-notice spreadsheet-viewer-sync-notice--ok' : 'spreadsheet-viewer-sync-notice spreadsheet-viewer-sync-notice--err'}>
            {sheetSyncNotice.text}
          </div>
        )}

        <div className="spreadsheet-viewer-body">
          <nav className="spreadsheet-viewer-sidebar" aria-label="Workbook sections">
            {navGroups.map((group) => (
              <div key={group.id} className="spreadsheet-viewer-nav-group">
                <div className="spreadsheet-viewer-nav-group-label">{group.label}</div>
                {group.names.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className={[
                      'spreadsheet-viewer-tab',
                      `spreadsheet-viewer-tab-cat-${group.id}`,
                      activeSheet === name ? 'spreadsheet-viewer-tab-active' : '',
                    ].join(' ')}
                    onClick={() => openSheet(name)}
                  >
                    {tabIcon(name) ? `${tabIcon(name)} ` : ''}
                    {tabLabel(name)}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div className="spreadsheet-viewer-main">
            {documentId === '1' && activeFy && !activeFy.isFirst && (
              <div className="spreadsheet-viewer-fy-bar">
                <span className="spreadsheet-viewer-fy-hint">
                  {`Second set of books — ${activeFy.label} (${activeFy.rangeLabel}, FYE ${activeFy.periodTo}). `}
                  Opening balances are carried forward from the prior year; year-2 depreciation is held at $0.00 pending a CCA policy.
                </span>
              </div>
            )}

            {documentId && (
              <FloatingAccountantCLI
                documentId={documentId}
                currentSheet={activeSheet}
                onEditsApplied={() => fetchSheets(true)}
              />
            )}

            {activeSheet === 'BANK_POSITION' ? (
              <ErrorBoundary key="bank-panel"><BankPositionPanel fy={fiscalYear} onRefreshSheets={() => fetchSheets(true)} /></ErrorBoundary>
            ) : activeSheet === 'BMO_POSITION' ? (
              <ErrorBoundary key="bmo-panel"><BmoPositionPanel fy={fiscalYear} onRefreshSheets={() => fetchSheets(true)} /></ErrorBoundary>
            ) : activeSheet === 'LOAN_POSITION' ? (
              <ErrorBoundary key="loan-panel"><LoanPositionPanel onRefreshSheets={() => fetchSheets(true)} /></ErrorBoundary>
            ) : activeSheet === 'CASH_FLOW' ? (
              <ErrorBoundary key="cf-panel"><CashFlowPanel fy={fiscalYear} /></ErrorBoundary>
            ) : activeSheet === 'SR&ED' ? (
              <ErrorBoundary key="sred-panel">
                <SReDPanel fy={fiscalYear} />
              </ErrorBoundary>
            ) : activeSheet === 'INVOICING' ? (
              <ErrorBoundary key="inv-panel"><InvoicingPanel fy={fiscalYear} fyWindow={activeFy} /></ErrorBoundary>
            ) : RESIZABLE_FINANCIAL_STATEMENT_SHEETS.includes(activeSheet) ? (
              <ResizableStatementTable sheetName={activeSheet} rows={rows} colCount={colCount} />
            ) : (
              <div className="spreadsheet-viewer-table-wrap">
                <table className="spreadsheet-viewer-table">
                  <tbody>
                    {rows.map((row, rIdx) => (
                      <tr key={rIdx}>
                        {row.map((cell, cIdx) => {
                          const isNum = (typeof cell === 'number' && !Number.isNaN(cell)) ||
                            (typeof cell === 'string' && cell.trim() !== '' && !Number.isNaN(Number(cell)));
                          const isEmpty = cell === '' || cell == null;
                          const isAmountColumn = colCount >= 2 && cIdx === 1;
                          const isSoCategory = isStatementOfOperationsCategoryCell(activeSheet, cIdx, cell);
                          return (
                            <td
                              key={cIdx}
                              className={`spreadsheet-viewer-cell${isNum || isAmountColumn ? ' spreadsheet-viewer-cell-num' : ''}${rIdx === 0 ? ' spreadsheet-viewer-cell-header' : ''}${isEmpty ? ' spreadsheet-viewer-cell-empty' : ''}${isSoCategory ? ' spreadsheet-viewer-cell-so-category' : ''}`}
                            >
                              {isEmpty ? '\u00A0' : String(cell)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="spreadsheet-viewer-page">
      {toolbar}
      <div className="spreadsheet-viewer-binary-hint">
        <p>Unknown or unsupported viewer type for this file.</p>
        <p>
          <a href={fileUrl} className="spreadsheet-viewer-download-link" target="_blank" rel="noopener noreferrer">
            Open or download file
          </a>
        </p>
      </div>
    </div>
  );
}

export default SpreadsheetViewerPage;
